import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Spin, Tooltip } from "antd";
import { fetchOrderUnits, setDeclaredValue, bulkUpsertUnits } from "../../api/OrderItemUnits";
import { groupItems, saveOrderItems, deleteItem } from "../../api/OrdersApi";
import { fetchAccountDims } from "../../api/Account";
import { renderAlertMessage } from "../../helpers/common";
import SummaryBar from "./SummaryBar";
import ItemCard from "./ItemCard";
import FlatUnitsView from "./FlatUnitsView";
// Order-level label popup, reused for the OFF (non-LPN) case of the per-unit
// "Print label" and bulk "Download labels" actions. Rendered hidden here and
// triggered via context.openOrderLabelPopup. It branches on org config itself
// (LPN vs template popup), same as the order-details "Print Labels".
import PrintLabels from "../orders/PrintLabels";
import {
  applyFieldChange,
  applySelectChange,
  buildDraft,
  buildItemPayload,
  buildNewItemPayload,
  draftEquals,
  isEmptyDraft,
} from "./itemDraftHelpers";
import {
  computeHandlingUnitsPreview,
  computeLiveMismatches,
  computeNewItemUnitsPreview,
} from "./UnitsTable";
import { itemVolumeFt3, itemWeightLb, itemDimWeightLb, computeDraftTotals } from "./itemMeasurePreview";
import "./ItemsMeasureUnits.scss";

// Exposed to nested components (ItemCard, UnitsTable, ...) so they can call
// reload()/read shared order context without prop-drilling through every
// level. Server is the single source of truth for everything EXCEPT item
// drafts (Part A): any write anywhere in the tree should end with a call to
// reload() rather than local derivation.
const ItemsMeasureUnitsContext = createContext({ reload: () => {}, items: [] });
const useItemsMeasureUnitsContext = () => useContext(ItemsMeasureUnitsContext);

// Task 5: shared with computeLiveUnitTotals below (out of scope for the
// itemMeasurePreview consolidation) — kept local rather than imported so that
// function's own math is left byte-for-byte untouched.
function toInches(value, uom) {
  const num = parseFloat(value || 0);
  if (Number.isNaN(num)) return 0;
  return uom === "cm" ? num / 2.54 : num;
}

// The v2/order_item_units index payload has no volume figure (backend never
// derives it). Per the plan, derive a display-only ft3 total from each item's
// L x W x H x handling_units, same inputs the mockup used, so the tile isn't
// blank. Never fed back to the server. itemVolumeFt3 now lives in the shared
// itemMeasurePreview module (Task 5) so this and computeDraftTotals can never
// diverge.
function computeVolumeFt3(items) {
  return (items || []).reduce((sum, item) => sum + itemVolumeFt3(item), 0);
}

// Live scannable-unit rollups (user request: Booked/Re-Weight/Re-Dim/Chargeable
// must reflect unit values as they're typed). Per unit, the displayed value is
// the persisted field overlaid with any in-flight edit — from the deferred
// `pendingUnitEdits.existing` bucket AND the `liveUnitValues` display map
// UnitRow writes on every keystroke in the immediate-save path — so both edit
// paths update the totals live. Reweigh sums are PARTIAL (a unit with no value
// contributes 0 instead of blanking the whole tile); booked/reweigh dim weights
// use L×W×H / dimFactor.
function unitToLb(value, uom) {
  const n = parseFloat(value) || 0;
  return ["kg", "kgs"].includes(String(uom || "").toLowerCase()) ? n / 0.453592 : n;
}
function present(v) {
  return v !== null && v !== undefined && v !== "";
}
function computeLiveUnitTotals(items, pendingNewItems, pendingUnitEdits, liveUnitValues, dimFactor, itemDrafts, dimWeightBasis) {
  const hasFactor = parseFloat(dimFactor) > 0;
  const factor = hasFactor ? parseFloat(dimFactor) : null;
  let bookedLb = 0;
  let bookedDimLb = 0;
  let reWtLb = 0;
  let reDimLb = 0;
  let unitCount = 0;
  let anyBookedDim = false;
  // PO point 5: Re-Weight / Re-Dim are ALL-or-nothing over the units in scope
  // (mirrors OrderReweighRollupService: the rollup is null unless EVERY unit
  // has the measurement). Counting how many units carry each measurement lets
  // us blank the tile unless all of them do — for the order panel that means
  // every unit across all items; for a single item's liveMeta it means every
  // unit on that item (so item cards keep their own per-item value).
  let reWtCount = 0;
  let reDimCount = 0;
  const dimWeight = (l, w, h, uom, f) =>
    (toInches(l, uom) * toInches(w, uom) * toInches(h, uom)) / f;
  // Accumulate one unit given a field getter `g` (already overlays persisted /
  // seed value < pending edit < live keystroke). `itemFactor` is the unit's
  // OWN item's dim factor so a per-item Dim dropdown change moves the reweigh-
  // dim (and booked-dim) rollup live; falls back to the shared account factor.
  const addUnit = (g, itemFactor) => {
    const uf = parseFloat(itemFactor) > 0 ? parseFloat(itemFactor) : factor;
    const hasUf = uf != null && uf > 0;
    unitCount += 1;
    bookedLb += unitToLb(g("booked_weight"), g("booked_weight_uom"));
    const bl = g("booked_length");
    const bw = g("booked_width");
    const bh = g("booked_height");
    if (hasUf && present(bl) && present(bw) && present(bh)) {
      anyBookedDim = true;
      bookedDimLb += dimWeight(bl, bw, bh, g("booked_dim_uom"), uf);
    }
    const rw = g("reweigh_weight");
    if (present(rw)) {
      reWtCount += 1;
      reWtLb += unitToLb(rw, g("reweigh_weight_uom"));
    }
    const rl = g("reweigh_length");
    const rwi = g("reweigh_width");
    const rh = g("reweigh_height");
    if (present(rl) && present(rwi) && present(rh)) {
      reDimCount += 1;
      if (hasUf) reDimLb += dimWeight(rl, rwi, rh, g("reweigh_dim_uom"), uf);
    }
  };
  // Persisted items: unit field < item-draft re-derivation < deferred pending
  // edit < live keystroke value.
  //
  // The item-draft re-derivation (draftDims below) mirrors the server's
  // apply_booked_defaults: a NON-manual unit's booked dims follow the item's
  // L×W×H, so an in-progress item-form dim edit moves this rollup immediately
  // — before Save re-syncs the units — instead of leaving the order panel
  // stuck on the persisted unit dims while the item card (which reads the
  // draft) already moved. A manually-edited unit (booked_dim_manual) or one
  // with a live/pending override keeps its own dims (ov wins over draftDims).
  (items || []).forEach((item) => {
    const existing = ((pendingUnitEdits && pendingUnitEdits[item.id]) || {}).existing || {};
    const draft = (itemDrafts && itemDrafts[item.id] && itemDrafts[item.id].draft) || null;
    // This item's own dim factor (draft selection wins, else persisted, else the
    // shared account factor) so its units' reweigh/booked dim use it.
    const itemFactor = (draft && draft.dim_factor) || (item && item.dim_factor) || dimFactor;
    (item.units || []).forEach((u) => {
      const ov = { ...(existing[u.id] || {}), ...((liveUnitValues && liveUnitValues[u.id]) || {}) };
      const draftDims =
        draft && !u.booked_dim_manual
          ? {
              booked_length: draft.item_length,
              booked_width: draft.item_width,
              booked_height: draft.item_height,
              booked_dim_uom: draft.item_length_uom,
            }
          : null;
      addUnit((field) => {
        if (ov[field] !== undefined) return ov[field];
        if (draftDims && draftDims[field] !== undefined) return draftDims[field];
        return u[field];
      }, itemFactor);
    });
  });
  // Create-flow pending new items: their seed units derive dims/weight from the
  // item draft (computeNewItemUnitsPreview), overlaid with per-unit edits in the
  // `added` bucket — so editing the item OR a seed unit both move the totals,
  // just like a persisted item. Without this, the create-flow summary panel
  // (which has no persisted `items`) never reflected scannable-unit edits.
  (pendingNewItems || []).forEach((p) => {
    if (!p || !p.draft) return;
    const seed = computeNewItemUnitsPreview({ draft: p.draft, readOnly: false }).newUnitPreviews || [];
    const added = ((pendingUnitEdits && pendingUnitEdits[p.id]) || {}).added || {};
    const itemFactor = (p.draft && p.draft.dim_factor) || dimFactor;
    seed.forEach((s, i) => {
      const ov = added[i] || {};
      const base = {
        booked_weight: s.weight,
        booked_weight_uom: s.weightUom || "lb",
        booked_length: s.length,
        booked_width: s.width,
        booked_height: s.height,
        booked_dim_uom: s.dimUom,
      };
      addUnit((field) => (ov[field] !== undefined ? ov[field] : base[field]), itemFactor);
    });
  });
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  // All-units gate (PO point 5): a reweigh total shows only when every unit in
  // scope carries that measurement; otherwise the tile is blank (null).
  const allReWt = unitCount > 0 && reWtCount === unitCount;
  const allReDim = unitCount > 0 && reDimCount === unitCount;

  // Booked DIM weight = PER ITEM ((L×W×H)/dim_factor) × count, where count is
  // pieces ('quantity') or handling units ('handling_units') per the org
  // config, summed across items (draft overlay) + pending new items (user
  // formula). Item-level — not a per-unit sum — so it matches exactly what the
  // backend persists (get_dim_values/update_dim_values). Uses the item draft
  // when present so item-dim / Pieces / HUs edits move it live.
  let bookedDim = 0;
  let anyDim = false;
  const addItemDim = (src) => {
    // Use the item's OWN selected dim factor (from its draft/persisted value) so
    // changing the per-item Dim dropdown moves the order-panel DIM weight live;
    // fall back to the shared account factor when the item has none.
    const itemFactor = (src && src.dim_factor) || dimFactor;
    const d = itemDimWeightLb(src, itemFactor, dimWeightBasis);
    if (d != null) {
      bookedDim += d;
      anyDim = true;
    }
  };
  (items || []).forEach((item) => {
    addItemDim((itemDrafts && itemDrafts[item.id] && itemDrafts[item.id].draft) || item);
  });
  (pendingNewItems || []).forEach((p) => {
    if (p && p.draft) addItemDim(p.draft);
  });

  return {
    unitCount,
    bookedWeightLb: round2(bookedLb),
    bookedDimLb: hasFactor && anyDim ? round2(bookedDim) : null,
    reweighWeightLb: allReWt ? round2(reWtLb) : null,
    reweighDimLb: hasFactor && allReDim ? round2(reDimLb) : null,
  };
}

let newItemSeq = 0;
function nextNewItemId() {
  newItemSeq += 1;
  return `new-${newItemSeq}`;
}

// Editable-units fix (docs/order-item-units-frontend-plan-2): stable empty
// default for an item with no pending unit edits — same "shared empty
// object" convention UnitsTable/ItemCard use for their own preview/edits
// defaults, so a lookup miss never hands a fresh {} down the tree.
const EMPTY_ITEM_UNIT_EDITS = { existing: {}, added: {} };
// Shared frozen empties so a per-card fallback ({} / []) keeps a STABLE
// reference across renders — required for React.memo(ItemCard/UnitRow) to skip
// an untouched card. Frozen because they're shared across every card; ItemCard
// only reads mismatches/units/touched (verified), never mutates them.
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

function hasPendingUnitEditsEntry(entry) {
  return !!entry && (Object.keys(entry.existing || {}).length > 0 || Object.keys(entry.added || {}).length > 0);
}

// Group Items (Figma "Group Order" frames, legacy ItemGrouping.jsx backend):
// clusters `items` by whatever group key `keyFn` returns for each item — a
// falsy key (item_group_id is '' for an ungrouped legacy item, or absent
// from pendingGroupOf) means "not in a group". A key claimed by only ONE
// item is not a real group (matches the legacy single-item-array = ungroup
// convention in stamp_order_items_group_id) — it's folded back into
// `ungrouped` rather than rendered as its own one-card "GROUP N". Used both
// for the SAVED clustering (keyed off item.item_group_id, shown in normal
// Items-view mode per the "Filled Grouped" frame) and the PENDING clustering
// while groupMode is active (keyed off local pendingGroupOf).
function deriveGroupClusters(items, keyFn) {
  const groupsByKey = {};
  const order = [];
  (items || []).forEach((item) => {
    const key = keyFn(item);
    if (key) {
      if (!groupsByKey[key]) {
        groupsByKey[key] = [];
        order.push(key);
      }
      groupsByKey[key].push(item);
    }
  });
  const realGroups = order.map((key) => groupsByKey[key]).filter((g) => g.length > 1);
  const groupedIds = new Set(realGroups.reduce((all, g) => all.concat(g.map((i) => i.id)), []));
  const ungrouped = (items || []).filter((item) => !groupedIds.has(item.id));
  return { realGroups, ungrouped };
}

const GroupIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
    <path d="M20 21H4" />
  </svg>
);

const UngroupIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="7 14 12 9 17 14" />
    <line x1="12" y1="9" x2="12" y2="21" />
    <path d="M20 3H4" />
  </svg>
);

// Header symbol pulled from Figma ("Cube Order" icon, node's brand-indigo
// #586FCC cube) — sits before the "Items & Measure" title.
const CubeIcon = () => (
  <svg width="21" height="24" viewBox="0 0 19.0527 21.5357" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M8.77637 0.200962C9.24047 -0.0669873 9.81227 -0.0669873 10.2764 0.200962L18.3027 4.83475C18.7667 5.10269 19.0527 5.59781 19.0527 6.13358V15.4021C19.0526 15.9379 18.7667 16.4331 18.3027 16.701L10.2764 21.3348C9.81229 21.6027 9.24046 21.6027 8.77637 21.3348L0.75 16.701C0.286019 16.4331 0.000149204 15.9379 0 15.4021V6.13358C6.59574e-05 5.59776 0.285958 5.10267 0.75 4.83475L8.77637 0.200962ZM1.5 15.4021L8.77637 19.6023V11.1961L1.5 6.91581V15.4021ZM10.2764 11.1961V19.6023L17.5527 15.4021V6.91581L10.2764 11.1961ZM2.32129 5.65897L9.52637 9.89725L16.7305 5.65897L9.52637 1.49979L2.32129 5.65897Z"
      fill="#586FCC"
    />
  </svg>
);

const ItemsMeasureUnits = (props) => {
  const {
    currentOrg,
    accountId,
    currentOrder = {},
    actionType,
    typeOfOrder,
    orgSettings = {},
    screenFrom,
    config,
    form,
    getDataRef,
    refreshDetails,
    updateItemDetails,
    onDeclaredValueSave,
    showActions = true,
    isShipper = false,
    // Reported: "if we have the error in the items and measure form. it should
    // not allow the save order in disable mode. and also for entire edit."
    // The surrounding Create/Edit-Order form owns the order Save; this box
    // reports its blocking-mismatch state up so that button can disable itself
    // (and the submit can abort) whenever an item here can't be saved.
    onBlockedChange,
    // Entire-order-edit: that flow owns the Save button (this box shows none),
    // and getDataRef tells the order update to skip items — so the order form
    // needs a way to flush this box's item/unit drafts. It passes a ref here;
    // we point it at handleSaveAll so the form can `await saveRef.current()`
    // before updating the order. Unused in inline-edit (box Save) and create
    // (getDataRef carries the items).
    saveRef,
    // required_dims (org_billing_rules) passed straight from the surrounding
    // Create Order form. The units payload also carries required_dims, but that
    // only loads for an EXISTING order — so in the CREATE flow (no orderId, no
    // payload) requiredDims stayed false and the dims-required blocker never
    // fired on Done. This prop seeds it for create; edit still gets it from the
    // payload (loadUnits/applyUnitsPayload).
    requiredDimsConfig = false,
  } = props;

  const orderId = currentOrder?.id;
  const orderDetails = currentOrder?.customer_order_details || {};

  // Legacy ItemsMeasure.jsx keys its own isReadOnly off actionType==="view"
  // (with an in-component Edit-button toggle that this simpler screen
  // originally didn't replicate) and separately hides all actions when the
  // caller passes showActions === false (shipper viewers, locked/deleted
  // orders — see ViewOrderScreen's showActionsFunc). Folding both into one
  // flag is the "single readOnly derivation" this screen exposes to ItemCard.
  const readOnly = actionType === "view" || showActions === false;

  // Org config (Org Settings -> Rules -> "Items & Measure Fields"): a
  // multi-select of field keys the org chose to HIDE. Stored as an
  // OrganizationConfig value delivered in orgSettings.items_measure_hidden_fields
  // (comma-separated string or array). Parsed once into a lookup the render
  // uses to drop the corresponding item-card fields / Dec. Value tile / Group
  // Items entry. `handling_unit_type` and `piece_type` are independent keys; a
  // legacy combined "hu_piece_type" value still hides both.
  const hiddenFields = useMemo(() => {
    const raw = orgSettings?.items_measure_hidden_fields;
    let list = [];
    if (Array.isArray(raw)) list = raw;
    else if (typeof raw === "string" && raw.trim()) {
      list = raw.split(",").map((s) => s.trim()).filter(Boolean);
    }
    const set = new Set(list);
    return {
      model: set.has("model"),
      serial_number: set.has("serial_number"),
      storage_location: set.has("storage_location"),
      declared_value: set.has("declared_value"),
      group_items: set.has("group_items"),
      // Handling Unit Type and Piece Type are independent toggles now (both
      // still honor the legacy combined "hu_piece_type" key if present).
      handling_unit_type: set.has("handling_unit_type") || set.has("hu_piece_type"),
      piece_type: set.has("piece_type") || set.has("hu_piece_type"),
    };
  }, [orgSettings]);

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  // Order number + warehouse code from the index payload — feed the live LPN
  // preview (<warehouse_code>-<order_number>-<seq>) shown on not-yet-saved unit
  // rows. Blank until the order exists (create flow), where the row keeps its
  // "on Save" placeholder instead.
  const [orderNumber, setOrderNumber] = useState("");
  const [warehouseCode, setWarehouseCode] = useState("");
  // Account-level DIM factor from the index payload (Dim config, legacy source)
  // — the fallback for the live DIM WT preview when an item has no dim_factor.
  const [accountDimFactor, setAccountDimFactor] = useState(null);
  // The account's configured DIM-factor options — populates the per-item Dim
  // dropdown on each card.
  const [configuredDimFactors, setConfiguredDimFactors] = useState([]);
  // Org configs fetched alongside the dim factor (see items_measure_units_
  // payload): whether item L/W/H are mandatory, and which multiplier DIM weight
  // uses ('quantity' pieces vs 'handling_units'). Default to the backend
  // defaults (not required; quantity-based) until the first payload lands.
  const [requiredDims, setRequiredDims] = useState(false);
  // Create flow (no order yet, so no units payload to carry required_dims):
  // seed requiredDims from the org_billing_rules config the surrounding Create
  // Order form passes down, so the dims-required Done/Save block works during
  // creation too. Edit still gets it authoritatively from the payload.
  useEffect(() => {
    if (!orderId) setRequiredDims(!!requiredDimsConfig);
  }, [orderId, requiredDimsConfig]);
  // Org setting require_items_on_order_creation: gates the mandatory-weight rule
  // (per-item) the same way requiredDims gates mandatory L/W/H. Read straight
  // from orgSettings (a static config), not the units payload.
  const requireItems = orgSettings?.require_items_on_order_creation === "true";
  const [dimWeightBasis, setDimWeightBasis] = useState("quantity");
  // Trigger for the shared order-level label popup (used by the per-unit "Print
  // label" and bulk "Download labels" actions when print_label_based_on_lpn is
  // OFF). Set true -> the hidden PrintLabels below opens its popup for the order.
  const [labelPopupClicked, setLabelPopupClicked] = useState(false);
  const openOrderLabelPopup = useCallback(() => setLabelPopupClicked(true), []);
  const [viewMode, setViewMode] = useState("items");
  const [loading, setLoading] = useState(false);
  const [addQty, setAddQty] = useState(1);
  // Box-local inline Edit/Save toggle (user-requested extension): lets a
  // viewer unlock JUST this box without the whole order entering edit mode.
  // Only offered when the box is readOnly for VIEW reasons and the caller
  // otherwise permits actions (same showActions/isShipper convention legacy
  // ItemsMeasure.jsx's own Edit button gates on) — a shipper viewer or a
  // showActions===false context (locked/deleted order) never sees it, so
  // toggling it can never surface actions those contexts intend to block.
  const [inlineEditing, setInlineEditing] = useState(false);
  const canInlineEdit = readOnly && showActions !== false && !isShipper;
  // Every field/unit-cell/bulk/swap/Dec.Value disable check and the
  // Add-N-Items row visibility flow from THIS value instead of the raw
  // `readOnly` above — flipping inlineEditing on temporarily unlocks the box
  // without touching actionType/showActions or any legacy screen.
  const effectiveReadOnly = readOnly && !(canInlineEdit && inlineEditing);

  // Inline-edit only: delete/duplicate of a PERSISTED item are DEFERRED to Save
  // (staged locally, no API) instead of hitting the item API immediately —
  // matching the field/unit draft model so nothing persists until Save and
  // Cancel discards everything. Entire-edit / create keep the current behavior
  // (create's new-item delete/duplicate is already local via onRemoveNew /
  // onDuplicateNew). `pendingDeleteIds` holds persisted ids staged for removal.
  const deferMutations = canInlineEdit && inlineEditing;
  const [pendingDeleteIds, setPendingDeleteIds] = useState(() => new Set());
  // Persisted items minus the ones staged for deletion — the list every render
  // path, summary rollup, block-check and count reads so a staged-deleted item
  // vanishes from the UI/totals immediately, before Save actually deletes it.
  const visibleItems = useMemo(
    () => (pendingDeleteIds.size === 0 ? items : items.filter((it) => !pendingDeleteIds.has(it.id))),
    [items, pendingDeleteIds]
  );

  // Part A: container-owned item drafts. `itemDrafts` is keyed by PERSISTED
  // item id -> { draft, touched }, only populated once a card is actually
  // edited (an item with no entry here just renders buildDraft(item), i.e.
  // "no local override"). `pendingNewItems` are local-only draft cards from
  // "Add N Items" with temp ids (`new-<n>`) — nothing is sent to the server
  // until Save. `collapsedItems` is the lifted collapse-state map so
  // switching Items <-> Scannable Units or a reload never resets it.
  const [itemDrafts, setItemDrafts] = useState({});
  const [pendingNewItems, setPendingNewItems] = useState([]);
  const [collapsedItems, setCollapsedItems] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  // B1: lock every item/unit input while a Save-All flush is in flight. Without
  // this a keystroke made mid-save lands in itemDrafts/pendingUnitEdits and is
  // then blanket-deleted with the rest of the saved entry — a silently lost
  // edit. effectiveReadOnly alone ignored savingAll (only the button disabled).
  const inputsLocked = effectiveReadOnly || savingAll;
  // Create flow (no order yet): the Dec. Value input is editable and its typed
  // value is held here, then handed to the surrounding Create Order save via
  // getDataRef's measurements.declared_value (there's no order to PATCH yet).
  const [createDeclaredValue, setCreateDeclaredValue] = useState("");
  // Wave B review IMPORTANT-2: ids of items whose draft was just cleared by
  // a successful Save but whose post-Save GET hasn't landed yet. In that
  // window the ItemCard would otherwise fall back from the (now-gone) live
  // mismatch computation to the STALE pre-Save server `mismatches` from the
  // last GET — flashing warnings the Save just resolved. While an id is in
  // here its card renders NO server mismatches; the set is cleared when the
  // reload that follows Save resolves (state-backed, not a ref, so the
  // clear re-renders the cards with the fresh server truth).
  const [mismatchSuppressedIds, setMismatchSuppressedIds] = useState(() => new Set());

  // Editable-units fix: pendingUnitEdits[itemId] = { existing: { [unitId]:
  // {field: value} }, added: { [index]: {field: value} } }. Keyed by
  // PERSISTED item id for an existing item, or the pending-new-item's temp
  // id (`new-<n>`) for a not-yet-created one — mirrors itemDrafts' own
  // keying. Populated only from UnitRow's onPendingEdit while an item's
  // units table is previewing (ItemCard's deferSaves); flushed against the
  // Save's reloaded, authoritative unit list in handleSaveAll below, then
  // cleared for whatever succeeded (kept for whatever failed, same
  // convention itemDrafts already uses).
  const [pendingUnitEdits, setPendingUnitEdits] = useState({});
  // Display-only live values for the top-panel rollups: unitId -> { field:
  // value } written by UnitRow on every keystroke (immediate-save path). NOT
  // persisted or flushed — purely so Booked/Re-Weight/Re-Dim/Chargeable update
  // as the user types. Cleared on each reload so the fresh persisted values
  // take over once a save lands.
  const [liveUnitValues, setLiveUnitValues] = useState({});
  const setLiveUnitValue = useCallback((unitId, fields) => {
    if (!unitId) return;
    setLiveUnitValues((prev) => ({ ...prev, [unitId]: { ...(prev[unitId] || {}), ...fields } }));
  }, []);
  // Task 7: counterpart to setLiveUnitValue for a failed unit save — drops
  // this unit's display-only overlay so the top-panel Booked/Re-Weight/
  // Re-Dim/Chargeable rollups fall back to the last persisted value instead
  // of continuing to reflect a value the server just rejected.
  const clearLiveUnitValue = useCallback(
    (unitId) =>
      setLiveUnitValues((prev) => {
        if (!prev[unitId]) return prev;
        const next = { ...prev };
        delete next[unitId];
        return next;
      }),
    []
  );

  // Group Items (legacy grouping backend, new checkbox UI): `groupMode` is
  // the separate mode toggled by the header's "Group Items" entry button.
  // `selectedIds` is the current checkbox selection (persisted item ids
  // only — pending "Add Item" cards never enter group mode, they have no
  // real id yet). `pendingGroupOf` is the local-only draft grouping map
  // (itemId -> a locally-generated group key, or absent if ungrouped),
  // seeded from each item's server `item_group_id` on entering group mode /
  // Reset, mutated by Group/Ungroup clicks, and only sent to the server —
  // via the existing legacy `groupItems` API — on Save.
  const [groupMode, setGroupMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pendingGroupOf, setPendingGroupOf] = useState({});
  const [savingGroups, setSavingGroups] = useState(false);

  // Guards every setState that lands after an await/then: unmounting
  // mid-flight (navigating away, switching orders) must not fire React's
  // unmounted-setState warning. Lives here (not in the fetch effect) so the
  // manual reload() calls the rest of the tree makes through context are
  // covered too. Same pattern as InvoiceContact.jsx / OptionsSectionView.jsx.
  const isMounted = useRef(true);

  // Mirrors `items` state so handleSaveAll's flush step (below) can read the
  // freshest reloaded item/unit list synchronously inside an async function,
  // regardless of whether ITS OWN loadUnits() call was the one that actually
  // committed it (a concurrent reload elsewhere in the tree can win the
  // race — see loadUnits' requestSeqRef staleness guard) — reading state
  // directly in a closure would otherwise see the value from when
  // handleSaveAll started, not after its `await loadUnits()` resolved.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Editable-units fix: generic setter for one row's pending edits, shared by
  // every ItemCard via a per-item bound closure (see the items.map below).
  // `kind` is "existing" (keyed by real unit id) or "added" (keyed by the
  // placeholder's index among preview.newUnitPreviews). `fields` merges into
  // whatever's already pending for that row; a value of `undefined` REMOVES
  // that field from the pending set (a field the user retyped back to
  // exactly its seed value has nothing left to override — see UnitRow, which
  // never actually sends `undefined` today, but the setter supports it for
  // any future "revert" affordance without needing a second code path).
  const setUnitPendingEdit = useCallback((itemId, kind, key, fields) => {
    setPendingUnitEdits((prev) => {
      const itemEntry = prev[itemId] || { existing: {}, added: {} };
      const bucket = { ...(itemEntry[kind] || {}) };
      const nextFields = { ...(bucket[key] || {}) };
      let changed = false;
      Object.keys(fields).forEach((f) => {
        const value = fields[f];
        if (value === undefined) {
          if (f in nextFields) {
            delete nextFields[f];
            changed = true;
          }
        } else if (nextFields[f] !== value) {
          nextFields[f] = value;
          changed = true;
        }
      });
      if (!changed) return prev;
      if (Object.keys(nextFields).length === 0) {
        delete bucket[key];
      } else {
        bucket[key] = nextFields;
      }
      return { ...prev, [itemId]: { ...itemEntry, [kind]: bucket } };
    });
  }, []);

  // Monotonic request id for loadUnits. Every save anywhere in the tree
  // (UnitRow cell PATCHes, bulk applies/swaps, the Save-all below, ...) ends
  // in a reload(); rapid edits therefore fire several overlapping GETs, and
  // nothing guarantees the responses resolve in request order. Only the
  // LATEST call may write state: an earlier GET's response landing after a
  // later one would overwrite items/summary with stale data and visually
  // revert a save the user just watched succeed.
  const requestSeqRef = useRef(0);

  // Render-isolation caches (persist across renders; keyed by item id):
  // - liveMetaCacheRef: memoizes each item's liveMeta by a per-item signature so
  //   one card's edit doesn't invalidate every card's rollup (byte-identical to
  //   recomputing — the signature covers every input computeLiveUnitTotals reads
  //   for a single item).
  // - draftFallbackRef: caches buildDraft(item) (pure over `item`) so an
  //   un-drafted card gets a stable `draft` ref instead of a fresh object.
  // - cardCbRef: stable per-item callback bundle; latestHandlersRef always holds
  //   this render's handlers so the bundle never goes stale (no closure over old
  //   state). Together these let React.memo actually skip untouched cards.
  const liveMetaCacheRef = useRef(new Map());
  const draftFallbackRef = useRef(new Map());
  const cardCbRef = useRef(new Map());
  const latestHandlersRef = useRef({});

  const loadUnits = useCallback(() => {
    if (!orderId) {
      // Create flow, pre-save: units don't exist yet. Skip the fetch instead
      // of hitting the API with a blank order_id.
      setSummary(null);
      setItems([]);
      return undefined;
    }
    requestSeqRef.current += 1;
    const requestId = requestSeqRef.current;
    setLoading(true);
    return fetchOrderUnits(orderId)
      .then((result) => {
        // Stale response (a newer loadUnits was issued while this one was
        // in flight) — discard it entirely; the newest request owns state.
        if (!isMounted.current || requestId !== requestSeqRef.current) return;
        if (result.success) {
          setSummary(result.summary || null);
          setAccountDimFactor(result.account_dim_factor ?? null);
          if (result.customer_order_number !== undefined) setOrderNumber(result.customer_order_number || "");
          if (result.warehouse_code !== undefined) setWarehouseCode(result.warehouse_code || "");
          if (result.required_dims !== undefined && result.required_dims !== null) setRequiredDims(!!result.required_dims);
          if (result.dim_weight_basis) setDimWeightBasis(result.dim_weight_basis);
          // Server items are merged in WITHOUT touching itemDrafts/
          // pendingNewItems/collapsedItems — those live in separate state
          // and are never derived from `items`, so a reload can never
          // clobber an in-progress draft (B-IMPORTANT-2, subsumed by this
          // container-owned draft model).
          setItems(result.items || []);
          // Fresh persisted units now carry the just-saved values, so drop the
          // display-only live overlay (it's stale the moment the reload lands).
          setLiveUnitValues({});
        } else {
          renderAlertMessage(result.errors);
        }
      })
      .finally(() => {
        // Same staleness rule for the spinner: only the latest request may
        // clear it, otherwise an early response would hide the spinner
        // while a newer fetch is still in flight.
        if (isMounted.current && requestId === requestSeqRef.current) {
          setLoading(false);
        }
      });
  }, [orderId]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  // Apply a full index-style payload ({ summary, account_dim_factor, items })
  // straight into state — the write endpoints (save_customer_order_items,
  // bulk_upsert) now return this exact shape, so Save refreshes the screen from
  // the write response instead of a follow-up reload GET. Bumps the request seq
  // so any loadUnits GET still in flight elsewhere (a unit-cell PATCH's reload,
  // a bulk apply, ...) is treated as stale and can't land afterwards and
  // clobber this fresher state.
  const applyUnitsPayload = useCallback((data) => {
    if (!data) return;
    requestSeqRef.current += 1;
    if (data.summary !== undefined) setSummary(data.summary || null);
    if (data.account_dim_factor !== undefined) setAccountDimFactor(data.account_dim_factor ?? null);
    if (data.customer_order_number !== undefined) setOrderNumber(data.customer_order_number || "");
    if (data.warehouse_code !== undefined) setWarehouseCode(data.warehouse_code || "");
    if (data.required_dims !== undefined && data.required_dims !== null) setRequiredDims(!!data.required_dims);
    if (data.dim_weight_basis) setDimWeightBasis(data.dim_weight_basis);
    if (data.items !== undefined) setItems(data.items || []);
    // Fresh persisted units now carry the just-saved values, so drop the
    // display-only live overlay (stale the moment this payload lands).
    setLiveUnitValues({});
  }, []);

  // Fetch the account's DIM config directly from /api/v2/dims — the SAME source
  // and shape the legacy order-item screens use (fetchAccountDims -> the account
  // Dim object). This drives the per-item Dim dropdown (configured_dim_factors)
  // and its default value (dim_factor) for BOTH create and edit flows, and backs
  // the DIM WT / Chargeable preview off the real account factor.
  useEffect(() => {
    const orgId = currentOrg && currentOrg.id;
    if (!orgId || !accountId) return undefined;
    let active = true;
    fetchAccountDims(orgId, accountId).then((result) => {
      if (!active || !isMounted.current) return;
      const dim = (result && result.configurations && result.configurations[0]) || null;
      if (!dim) return;
      const configured = dim.configured_dim_factors || [];
      const factor = configured.includes(dim.dim_factor) ? dim.dim_factor : configured[0] ?? dim.dim_factor;
      setAccountDimFactor(factor ?? null);
      setConfiguredDimFactors(configured);
    });
    return () => {
      active = false;
    };
  }, [orderId, accountId, currentOrg]);

  // B-CRITICAL-1: items are persisted entirely through this component's own
  // Save flow now (create_customer_order_item/update_customer_order_item),
  // never through the surrounding screen's whole-order save. Returning a
  // marker instead of {items, summary} tells the caller (CreateOrderScreen)
  // to omit item_params entirely and send items_managed_externally: true, so
  // the entire-update service's destroy/recreate block never runs against
  // this screen's item set.
  useEffect(() => {
    if (!getDataRef) return;
    getDataRef.current = () => {
      // EDIT flow (order exists): items are persisted directly via the item
      // API as they're edited, so tell the whole-order save to leave the item
      // set alone (skip its destroy/recreate block). Still hand it the LIVE item
      // count (persisted items + non-empty pending new-item cards) so the
      // surrounding "require at least one item" rule can be enforced without an
      // items array — otherwise that check reads 0 and false-fires on an order
      // that clearly has items (FEP require-items false-positive on entire-edit).
      if (orderId) {
        const persisted = visibleItems || [];
        const keptPending = pendingNewItems.filter(
          (p) => !isEmptyDraft(p.draft) || hasPendingUnitEditsEntry(pendingUnitEdits[p.id])
        );
        // items_missing_weight lets the surrounding "weight is mandatory" rule
        // still fire even though we don't hand back an items array: an item's
        // effective weight is its live draft weight (edited) or its persisted
        // item_weight; a pending new-item card counts as weighed if the user
        // entered per-unit weights (they derive the item weight on create).
        const hasWeight = (w) => (parseFloat(w) || 0) > 0;
        let itemsMissingWeight = 0;
        persisted.forEach((it) => {
          const d = itemDrafts[it.id] && itemDrafts[it.id].draft;
          const w = d ? itemWeightLb(d) : parseFloat(it.item_weight) || 0;
          if (!hasWeight(w)) itemsMissingWeight += 1;
        });
        keptPending.forEach((p) => {
          const w = hasPendingUnitEditsEntry(pendingUnitEdits[p.id]) ? 1 : itemWeightLb(p.draft);
          if (!hasWeight(w)) itemsMissingWeight += 1;
        });
        // Order-level rollup (weight/quantity/pallets/volume) recomputed from the
        // CURRENT items, sent alongside the marker. The whole-order entire-update
        // re-applies customer_order_params (CustomerOrderCreateService), so an
        // absent/stale weight there was overwriting order.weight to 0 right after
        // the item flush recomputed it — this hands the correct, live values so
        // the saved order matches the summary panel. Same shape the create branch
        // returns. dimFactor isn't needed for these totals, so pass null.
        const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
        const editTotals = computeDraftTotals(visibleItems, itemDrafts, pendingNewItems, null);
        const editWeightLb = r2(editTotals.weightLb);
        return {
          items_managed_externally: true,
          items_count: persisted.length + keptPending.length,
          items_missing_weight: itemsMissingWeight,
          measurements: {
            weight: editWeightLb,
            weight_in_kgs: r2(editWeightLb * 0.453592),
            weight_uom: "lbs",
            quantity: editTotals.pcs,
            pallets: editTotals.hus,
            cbm_volume: r2((editTotals.volumeFt3 || 0) * 0.0283168),
          },
        };
      }
      // CREATE flow (no order yet): hand the surrounding Create Order save the
      // item params so the items are created WITH the order (there's nothing
      // to PATCH against yet). Drop fully-empty draft cards, same predicate
      // Save uses elsewhere. Units are generated server-side from these on
      // create (OrderItem#sync_scannable_units).
      const orgId = currentOrg && currentOrg.id;
      const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
      // Keep any card the user actually touched — a non-empty item draft OR
      // per-unit scannable edits (they may have typed only in the unit rows,
      // leaving the item card's own Weight/Dims fields blank).
      const kept = pendingNewItems.filter(
        (p) => !isEmptyDraft(p.draft) || hasPendingUnitEditsEntry(pendingUnitEdits[p.id])
      );
      let orderWeightLb = 0;
      const itemParams = kept.map((p) => {
        const payload = buildNewItemPayload({ draft: p.draft, touched: p.touched, orderId: undefined, orgId, accountId });
        // Roll the scannable-unit rows up into the item so values entered per
        // unit (not just the item card's own Weight/Dims fields) persist —
        // otherwise an item weighed/measured per unit saves as 0. Sum the
        // booked weights across the seed units (their distributed defaults
        // overlaid with the user's per-unit edits); take the first unit's dims
        // for the item's single L/W/H.
        const seed = computeNewItemUnitsPreview({ draft: p.draft, readOnly: false }).newUnitPreviews || [];
        const added = (pendingUnitEdits[p.id] && pendingUnitEdits[p.id].added) || {};
        let derivedWeightLb = 0;
        seed.forEach((u, i) => {
          const ov = added[i] || {};
          const w = ov.booked_weight !== undefined ? ov.booked_weight : u.weight;
          const wu = ov.booked_weight_uom !== undefined ? ov.booked_weight_uom : u.weightUom;
          derivedWeightLb += unitToLb(w, wu);
        });
        derivedWeightLb = round2(derivedWeightLb);
        // Safety net: if the unit derivation produced nothing (no seed rows /
        // no unit weights), fall back to the item card's own entered weight so
        // card-only entry still persists.
        if (derivedWeightLb <= 0) derivedWeightLb = round2(itemWeightLb(p.draft));
        // Always set item_weight from the derived value (never leave the
        // buildNewItemPayload default) so the item weight and the order weight
        // below are computed from the same source and can't disagree.
        payload.item_weight = derivedWeightLb;
        payload.item_weight_uom = "lb";
        payload.item_weight_in_kgs = round2(derivedWeightLb * 0.453592);
        const ov0 = added[0] || {};
        const first = seed[0] || {};
        const dLen = ov0.booked_length !== undefined ? ov0.booked_length : first.length;
        const dWid = ov0.booked_width !== undefined ? ov0.booked_width : first.width;
        const dHgt = ov0.booked_height !== undefined ? ov0.booked_height : first.height;
        const dUom = ov0.booked_dim_uom !== undefined ? ov0.booked_dim_uom : first.dimUom;
        if ((parseFloat(dLen) || 0) > 0 || (parseFloat(dWid) || 0) > 0 || (parseFloat(dHgt) || 0) > 0) {
          payload.item_length = parseFloat(dLen) || 0;
          payload.item_width = parseFloat(dWid) || 0;
          payload.item_height = parseFloat(dHgt) || 0;
          if (dUom) {
            payload.dimensions_units = dUom;
            payload.item_length_uom = dUom;
          }
        }
        orderWeightLb += derivedWeightLb;
        return payload;
      });
      // Order-level measurements, same shape legacy ItemsMeasure returned
      // (spread into customer_order_params by CreateOrderScreen). Without
      // `weight` here the create fails validation ("Please provide order
      // weight", customer_order_create_service.rb). Uses the same rolled-up
      // item weights so the order weight matches the items being created.
      const totals = computeDraftTotals(items, itemDrafts, pendingNewItems, null);
      const weightLb = round2(orderWeightLb);
      const measurements = {
        weight: weightLb,
        weight_in_kgs: round2(weightLb * 0.453592),
        weight_uom: "lbs",
        quantity: itemParams.length,
        pallets: totals.hus,
        surface_area: 1,
        dimensions_units: "in",
        cbm_volume: round2((totals.volumeFt3 || 0) * 0.0283168),
        // Dec. Value typed into the (now editable) create-mode input; falls
        // back to whatever the order details already carried.
        declared_value: createDeclaredValue || orderDetails?.declared_value || "",
      };
      return { items: itemParams, measurements };
    };
  }, [getDataRef, orderId, pendingNewItems, items, itemDrafts, orderDetails, currentOrg, accountId, createDeclaredValue, pendingUnitEdits]);

  // CREATE flow: seed a single empty item card once so the user lands on an
  // item entry form immediately (matching the legacy screen / mockup), rather
  // than an empty box they have to "Add" into.
  const createSeededRef = useRef(false);
  useEffect(() => {
    if (orderId || createSeededRef.current) return;
    createSeededRef.current = true;
    setPendingNewItems((prev) =>
      prev.length === 0 ? [{ id: nextNewItemId(), draft: buildDraft({}), touched: {} }] : prev
    );
  }, [orderId]);

  const volumeFt3 = useMemo(() => computeVolumeFt3(items), [items]);
  const dimFactor = useMemo(() => {
    // Legacy get_dim_values rule: an item's OWN dim_factor wins; otherwise use
    // the account's configured DIM factor (its Dim config). accountDimFactor is
    // sourced from the index payload (edit flow) or fetched from account config
    // (create flow). If the account has no Dim config at all, there is no
    // factor to preview with — null propagates so the live DIM WT / Re-Dim /
    // Chargeable preview (and SummaryBar's dim tiles) render "—" instead of
    // silently assuming an industry-standard 139 the account never configured.
    const withFactor = items.find((item) => item?.dim_factor);
    if (withFactor) return withFactor.dim_factor;
    return accountDimFactor ?? null;
  }, [items, accountDimFactor]);

  const declaredValue = orderId
    ? summary?.declared_value ?? orderDetails?.declared_value ?? ""
    : createDeclaredValue;

  // Default Dec. Value save path: PATCH v2/order_item_units/set_declared_value.
  // Self-contained so the legacy render sites don't have to wire anything —
  // an onDeclaredValueSave prop (if a caller ever passes one) overrides it.
  // Returns the API result promise so SummaryBar can revert its input when
  // the save fails.
  const handleDeclaredValueSave = useCallback(
    (value) => {
      if (typeof onDeclaredValueSave === "function") {
        return onDeclaredValueSave(value);
      }
      if (!orderId) {
        // Create flow: no order to PATCH yet — just retain the typed value so
        // getDataRef hands it to the surrounding Create Order save.
        const next = value === null || value === undefined ? "" : String(value);
        setCreateDeclaredValue(next);
        return Promise.resolve({ success: true, declared_value: next });
      }
      return setDeclaredValue(orderId, value).then((result) => {
        if (!isMounted.current) return result;
        if (result.success) {
          // Task 4: the write response now carries a fresh authoritative
          // summary (Task 3) — prefer it over the optimistic
          // declared_value-only patch, which stays only as a fallback for a
          // response that (unexpectedly) omits summary.
          if (result.summary) {
            setSummary(result.summary);
          } else {
            setSummary((prev) => ({
              ...(prev || {}),
              declared_value: result.declared_value,
            }));
          }
          // B-M5: invalidate any GET already in flight when this save landed
          // — bumping the sequence counter makes loadUnits discard that
          // response (requestId won't match) instead of letting a stale
          // summary.declared_value overwrite the value the user just saved.
          requestSeqRef.current += 1;
        } else {
          renderAlertMessage(result.errors);
        }
        return result;
      });
    },
    [onDeclaredValueSave, orderId]
  );

  // "Add N Items" (Part A2): push N local draft cards, expanded, with no
  // backend call at all. They only become real items on Save.
  const handleAddItems = () => {
    if (effectiveReadOnly) return;
    const qty = Math.max(parseInt(addQty, 10) || 1, 1);
    const fresh = Array.from({ length: qty }, () => ({
      id: nextNewItemId(),
      draft: buildDraft({}),
      touched: {},
    }));
    setPendingNewItems((prev) => [...prev, ...fresh]);
  };

  const handleRemoveNewItem = (tempId) => {
    setPendingNewItems((prev) => prev.filter((p) => p.id !== tempId));
  };

  // Copy icon on a not-yet-saved card: clone its draft into a fresh new-item
  // card right after it (deep-copies draft/touched so the two edit
  // independently), so the copy affordance works during creation too.
  const handleDuplicateNewItem = (tempId) => {
    setPendingNewItems((prev) => {
      const idx = prev.findIndex((p) => p.id === tempId);
      if (idx === -1) return prev;
      const src = prev[idx];
      const clone = {
        id: nextNewItemId(),
        draft: { ...src.draft },
        touched: { ...src.touched },
      };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next;
    });
  };

  // ---- Inline-edit deferral (delete/duplicate staged until Save) ----

  // Stage a PERSISTED item for deletion — no API call; the item vanishes from
  // the list/totals (via visibleItems) and is deleted only on Save. Any
  // in-progress draft / unit edits for it are dropped so they can't resurrect
  // it or fire on Save. Cancel (handleCancelInlineEdit) clears the staged set.
  const stageDeleteItem = (id) => {
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setItemDrafts((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setPendingUnitEdits((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Duplicate a PERSISTED item into an editable local clone that behaves exactly
  // like an "Add Item" card, pre-filled with the source's item-level fields
  // (name/pieces/weight/dims/HUs). No API: it's created on Save through the
  // normal new-item batch. We deliberately do NOT carry the source's per-unit
  // values — a clone must behave "like new", so its units auto-distribute from
  // its pieces/weight; freezing the source's unit split/weights here made
  // editing pieces/weight produce piece-split / weight mismatches. Cancel drops
  // it with the rest of pendingNewItems.
  const stageDuplicateItem = (sourceItem) => {
    if (!sourceItem) return;
    const tempId = nextNewItemId();
    setPendingNewItems((prev) => [...prev, { id: tempId, draft: buildDraft(sourceItem), touched: {} }]);
    setCollapsedItems((prev) => ({ ...prev, [tempId]: false }));
  };

  // Field/select edits update the draft only — no server call (Part A1
  // removes ItemCard's old debounced auto-persist entirely).
  const handleItemFieldChange = (itemId, field, value) => {
    setItemDrafts((prev) => {
      const base = prev[itemId] || { draft: buildDraft(items.find((i) => i.id === itemId)), touched: {} };
      return {
        ...prev,
        [itemId]: { draft: applyFieldChange(base.draft, field, value), touched: { ...base.touched, [field]: true } },
      };
    });
  };

  const handleItemSelectChange = (itemId, field, value) => {
    setItemDrafts((prev) => {
      const base = prev[itemId] || { draft: buildDraft(items.find((i) => i.id === itemId)), touched: {} };
      const nextDraft = applySelectChange(base.draft, field, value);
      if (nextDraft === base.draft) return prev;
      return { ...prev, [itemId]: { draft: nextDraft, touched: { ...base.touched, [field]: true } } };
    });
  };

  const handleNewItemFieldChange = (tempId, field, value) => {
    setPendingNewItems((prev) =>
      prev.map((p) =>
        p.id === tempId
          ? { ...p, draft: applyFieldChange(p.draft, field, value), touched: { ...p.touched, [field]: true } }
          : p
      )
    );
  };

  const handleNewItemSelectChange = (tempId, field, value) => {
    setPendingNewItems((prev) =>
      prev.map((p) => {
        if (p.id !== tempId) return p;
        const nextDraft = applySelectChange(p.draft, field, value);
        if (nextDraft === p.draft) return p;
        return { ...p, draft: nextDraft, touched: { ...p.touched, [field]: true } };
      })
    );
  };

  // Collapse state is a per-id map storing an explicit boolean; an id absent
  // from it falls back to `defaultCollapsed`. Persisted cards default
  // COLLAPSED (user QA request); brand-new "Add Item" cards default EXPANDED
  // (you're entering them) but can still be collapsed. The toggle flips the
  // EFFECTIVE value so the first click always does the visible thing.
  const isItemCollapsed = (itemId, defaultCollapsed = true) =>
    collapsedItems[itemId] === undefined ? defaultCollapsed : collapsedItems[itemId];

  const toggleCollapse = (itemId, defaultCollapsed = true) => {
    setCollapsedItems((prev) => ({
      ...prev,
      [itemId]: !(prev[itemId] === undefined ? defaultCollapsed : prev[itemId]),
    }));
  };

  // Group Items: clusters derived from the SAVED item_group_id (rendered in
  // normal Items-view mode, per the "Filled Grouped" frame) and from the
  // local PENDING grouping map (rendered while groupMode is active).
  const savedGroupClusters = useMemo(
    () => deriveGroupClusters(visibleItems, (item) => item.item_group_id || null),
    [visibleItems]
  );
  const pendingGroupClusters = useMemo(
    () => deriveGroupClusters(items, (item) => pendingGroupOf[item.id] || null),
    [items, pendingGroupOf]
  );
  const pendingGroupedIdSet = useMemo(() => {
    const set = new Set();
    pendingGroupClusters.realGroups.forEach((g) => g.forEach((i) => set.add(i.id)));
    return set;
  }, [pendingGroupClusters]);

  // Header button gates: "Group (N) Items" needs >=2 selected (matches the
  // legacy single-item-array = ungroup convention — a 1-item "group" isn't
  // one); "Ungroup Items" needs at least one SELECTED id that's currently in
  // a real (>=2-member) pending group, exactly what the Figma "Ungroup
  // Selected" frame gates on.
  const selectedGroupedCount = Array.from(selectedIds).filter((id) => pendingGroupedIdSet.has(id)).length;
  const groupEnabled = selectedIds.size >= 2;
  const ungroupEnabled = selectedGroupedCount > 0;
  const groupBtnLabel = groupEnabled ? `Group (${selectedIds.size}) Items` : "Group Items";
  const ungroupBtnLabel = ungroupEnabled ? `Ungroup (${selectedGroupedCount}) Items` : "Ungroup Items";

  // Entry point (header's "Group Items" button, Figma "Filled Grouped"
  // frame): grouping is its OWN operation/mode — like the legacy "Group
  // items" button on the order-details view — so it does NOT require entering
  // field-edit mode first. It shows once the order is saved, in the base
  // Items view, with >=2 PERSISTED items, and whenever the user is allowed to
  // act on this order: a natively-editable screen (`!readOnly`) OR a view
  // screen where inline edit is permitted (`canInlineEdit`, i.e. showActions
  // and not a shipper). Group mode has its own Save/Cancel footer and never
  // touches item fields, so it's safe to enter straight from a view.
  // Grouping is a SEPARATE, view-only flow (product decision: "Remove Group
  // Items option when editing items"). It shows only on the locked details
  // view (effectiveReadOnly) for a user with edit permission (canInlineEdit) —
  // never while actively editing item fields or in the entire-order-edit flow.
  // Also suppressed when the org has hidden Group Items (see hiddenFields).
  const groupItemsEntryVisible =
    !!orderId &&
    viewMode === "items" &&
    items.length >= 2 &&
    effectiveReadOnly &&
    canInlineEdit &&
    !hiddenFields.group_items;

  const seedPendingGroupOf = () => {
    const seed = {};
    items.forEach((item) => {
      if (item.item_group_id) seed[item.id] = item.item_group_id;
    });
    return seed;
  };

  const handleEnterGroupMode = () => {
    setPendingGroupOf(seedPendingGroupOf());
    setSelectedIds(new Set());
    setGroupMode(true);
  };

  // Reset (footer): revert any pending grouping back to the server's own
  // item_group_id state, same seed the mode-entry above uses — does NOT
  // exit group mode.
  const handleResetGroups = () => {
    setPendingGroupOf(seedPendingGroupOf());
    setSelectedIds(new Set());
  };

  // Cancel (footer): exit group mode and discard every pending change —
  // nothing was ever sent to the server, so there's nothing to reload.
  const handleCancelGroupMode = () => {
    setGroupMode(false);
    setSelectedIds(new Set());
    setPendingGroupOf({});
  };

  const handleToggleSelectForGroup = (itemId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // "Group (N) Items": every currently-checked item joins ONE new local
  // group (a fresh key), overriding whatever pending group any of them was
  // already in — matches the Figma "State=Selected" -> "State=Grouped"
  // transition, where the two checked cards move under one new "GROUP 1"
  // and the checkboxes clear.
  const handleGroupSelected = () => {
    if (!groupEnabled) return;
    const key = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setPendingGroupOf((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = key;
      });
      return next;
    });
    setSelectedIds(new Set());
  };

  // "Ungroup Items": drop every checked item's pending group membership.
  // Any group left with a single member is already treated as ungrouped by
  // deriveGroupClusters (no cleanup of the orphaned key needed here).
  const handleUngroupSelected = () => {
    if (!ungroupEnabled) return;
    setPendingGroupOf((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
    setSelectedIds(new Set());
  };

  // Save (footer): translate the pending clustering into the legacy
  // groupItems payload — one subarray of ids per real (>=2-member) group,
  // plus one single-element subarray per ungrouped item (mirrors
  // ItemGrouping.jsx's own handleSave / OrderItems#stamp_order_items_group_id,
  // which keys off array LENGTH, not any explicit "ungroup" flag). On
  // success, reload() so the fresh item_group_id values land through the
  // normal GET, then exit group mode.
  const handleSaveGroups = () => {
    if (savingGroups) return;
    const groupPayload = pendingGroupClusters.realGroups.map((g) => g.map((i) => i.id));
    const singlePayload = pendingGroupClusters.ungrouped.map((i) => [i.id]);
    setSavingGroups(true);
    groupItems({
      customer_order_id: orderId,
      group_order_item_ids: [...groupPayload, ...singlePayload],
    })
      .then((result) => {
        if (!isMounted.current) return;
        if (result.success) {
          setGroupMode(false);
          setSelectedIds(new Set());
          setPendingGroupOf({});
          loadUnits();
        } else {
          renderAlertMessage(result.errors);
        }
      })
      .finally(() => {
        if (isMounted.current) setSavingGroups(false);
      });
  };

  // Any dirty existing-item draft, or any pending new-item card (even a
  // still-empty one — it's a "pending change" the header button should
  // reflect; Save silently drops the empty ones, see handleSaveAll).
  const isDirty = useMemo(() => {
    if (pendingNewItems.length > 0) return true;
    // A staged (deferred) delete is an unsaved change too, so Save must enable.
    if (pendingDeleteIds.size > 0) return true;
    return visibleItems.some((item) => {
      const entry = itemDrafts[item.id];
      return !!entry && !draftEquals(entry.draft, buildDraft(item));
    });
  }, [visibleItems, itemDrafts, pendingNewItems, pendingDeleteIds]);

  // B3: SummaryBar's Quantity/Volume tiles read this (null once clean) to
  // decide whether to show the live draft-derived totals or the ordinary
  // server-sourced numbers — see computeDraftTotals above and SummaryBar's
  // draftTotals prop.
  const draftTotals = useMemo(
    () => (isDirty ? computeDraftTotals(visibleItems, itemDrafts, pendingNewItems, dimFactor, dimWeightBasis) : null),
    [isDirty, visibleItems, itemDrafts, pendingNewItems, dimFactor, dimWeightBasis]
  );

  // Live + partial scannable-unit rollups fed to SummaryBar (Booked weight,
  // Re-Weight, Re-Dim, Chargeable) — recomputed whenever the units or the
  // pending booked edits change, so those tiles move as unit values are
  // entered instead of only after a full-order save. Null when the order has
  // no units yet (create flow), leaving the server/draft-sourced values.
  const liveUnitTotals = useMemo(
    () =>
      visibleItems.some((i) => (i.units || []).length > 0) || pendingNewItems.length > 0
        ? computeLiveUnitTotals(visibleItems, pendingNewItems, pendingUnitEdits, liveUnitValues, dimFactor, itemDrafts, dimWeightBasis)
        : null,
    [visibleItems, pendingNewItems, pendingUnitEdits, liveUnitValues, dimFactor, itemDrafts, dimWeightBasis]
  );

  // Per-item live rollup, memoized by a per-item signature (see liveMetaCacheRef
  // above). A card's liveMeta keeps the SAME reference across renders unless one
  // of THIS item's own inputs changed — so typing in item A no longer produces a
  // new liveMeta object for item B, letting React.memo(ItemCard) skip B.
  //
  // The signature must list every input computeLiveUnitTotals([item], []...)
  // reads for a single item: the item itself, this item's pending unit edits,
  // this item's draft, the two scalar configs, and — per unit — that unit's live
  // keystroke overlay. Each of these refs changes only when that specific slice
  // changes (the setters preserve untouched entries' identity), so the compare
  // is exact, not approximate.
  const getLiveMeta = (item) => {
    const units = item.units || EMPTY_ARRAY;
    const sig = [item, pendingUnitEdits[item.id], itemDrafts[item.id], dimFactor, dimWeightBasis];
    for (let i = 0; i < units.length; i += 1) sig.push(liveUnitValues[units[i].id]);
    const cached = liveMetaCacheRef.current.get(item.id);
    if (cached && cached.sig.length === sig.length && cached.sig.every((v, i) => v === sig[i])) {
      return cached.value;
    }
    const value = computeLiveUnitTotals([item], [], pendingUnitEdits, liveUnitValues, dimFactor, itemDrafts, dimWeightBasis);
    liveMetaCacheRef.current.set(item.id, { sig, value });
    return value;
  };

  // Stable buildDraft fallback for an un-drafted card: buildDraft is pure over
  // `item`, so cache by item id and reuse until the item object itself changes.
  const getFallbackDraft = (item) => {
    const cached = draftFallbackRef.current.get(item.id);
    if (cached && cached.item === item) return cached.draft;
    const draft = buildDraft(item);
    draftFallbackRef.current.set(item.id, { item, draft });
    return draft;
  };

  // True while the user is actively editing unit values — whether via the
  // immediate-save keystroke channel (liveUnitValues) OR the deferred /
  // create-flow pending buckets (pendingUnitEdits). Used to decide whether
  // Booked weight follows the live unit sum vs. the authoritative order total,
  // so a freshly-loaded order still shows server totals and only switches once
  // the user starts typing (in either channel).
  const hasLiveUnitEdits =
    Object.keys(liveUnitValues).length > 0 ||
    Object.values(pendingUnitEdits || {}).some(hasPendingUnitEditsEntry);

  // Audit fix I2 (minimal-scope FlatUnitsView lock): every persisted item id
  // that's currently either draft-dirty (Items view) or carrying unflushed
  // pendingUnitEdits — the same two conditions handleSaveAll below already
  // treats as "this item has something Save needs to persist." Recomputed
  // whenever any of those three pieces of state change; handed to
  // FlatUnitsView so it can render that item's rows read-only rather than
  // letting the flat view and the Items view disagree about a cell's value.
  const editingItemIds = useMemo(() => {
    const set = new Set();
    items.forEach((item) => {
      const entry = itemDrafts[item.id];
      const dirty = !!entry && !draftEquals(entry.draft, buildDraft(item));
      if (dirty || hasPendingUnitEditsEntry(pendingUnitEdits[item.id])) set.add(item.id);
    });
    return set;
  }, [items, itemDrafts, pendingUnitEdits]);

  // PO point 7 (edit border): a card counts as "unsaved" not only when its
  // item draft is dirty or it has deferred pending unit edits (editingItemIds),
  // but ALSO while the user is actively typing into its scannable units — those
  // land in liveUnitValues (keyed by unit id), so map each item's units through
  // it. This is what makes the border darken on a unit edit too, and it clears
  // once the reload after Save drops liveUnitValues.
  const dirtyCardIds = useMemo(() => {
    const liveUnitIds = new Set(Object.keys(liveUnitValues));
    const set = new Set(editingItemIds);
    items.forEach((item) => {
      if ((item.units || []).some((u) => liveUnitIds.has(String(u.id)))) set.add(item.id);
    });
    return set;
  }, [editingItemIds, items, liveUnitValues]);

  // A3: Save persists every pending change at once — createItem for each
  // non-empty pending new item, updateItem for each dirty existing item —
  // then ONE reload() and drafts are cleared for whatever succeeded. On
  // partial failure the failed drafts are kept (and their errors surfaced)
  // so the user can fix and retry without losing anything.
  //
  // Editable-units fix: extended with a second phase. An item can now also
  // carry pendingUnitEdits with NO item-field draft at all (the user only
  // typed into unit rows while HUs/Pieces/etc previewed dirty, then the item
  // draft happened to settle back to clean before Save) — `unitOnlyItems`
  // below catches that case so its pending unit edits still flush even
  // though no updateItem call is needed for it. After the item-level
  // save + reload (unchanged, still runs first, exactly as before), a
  // SECOND step resolves each item's pendingUnitEdits against the now-
  // authoritative reloaded units (existing[unitId] by real id, added[k] by
  // seq-order index — see the `preSaveUnitCounts` snapshot below) and issues
  // one updateUnit PATCH per resolved target, then reloads once more so the
  // table shows real LPNs/seqs instead of the preview placeholders.
  // The set of items the next Save would persist that currently have a live
  // piece-split/weight mismatch (the "This item can't be closed yet." panel).
  // Exactly the computation each card's panel renders, scoped to
  // dirty/unit-edited/pending-new items — used to DISABLE the Save button and
  // as handleSaveAll's abort gate.
  const collectBlockedItems = () => {
    const blockedNames = [];
    const blockedIds = [];
    // Per-item reasons ({ name, reasons: [...] }) so the surrounding Create/Save
    // button's tooltip can name the specific items and what each one needs.
    const blocked = [];
    const checkItem = ({ id, itemLike, draft, units }) => {
      const preview = itemLike
        ? computeHandlingUnitsPreview({
            units,
            draft,
            item: itemLike,
            readOnly: false,
            pendingEdits: (pendingUnitEdits[id] || EMPTY_ITEM_UNIT_EDITS).existing,
          })
        : computeNewItemUnitsPreview({ draft, readOnly: false });
      const bad = computeLiveMismatches({
        units,
        draft,
        preview,
        pendingEdits: pendingUnitEdits[id] || EMPTY_ITEM_UNIT_EDITS,
      });
      // Required DIMs (org billing rule): a blank item L/W/H also blocks Save,
      // matching the item card's own dims_required mismatch.
      const blankDim = (v) => v === "" || v === null || v === undefined;
      const dimsMissing =
        requiredDims && (blankDim(draft.item_length) || blankDim(draft.item_width) || blankDim(draft.item_height));
      // Required weight (org setting require_items_on_order_creation): a blank/
      // zero item weight also blocks Save, matching the card's weight_required
      // blocker. weight_total_exact is the draft's source-of-truth weight.
      const weightMissing = requireItems && !((parseFloat(draft.weight_total_exact) || 0) > 0);
      if (bad.length > 0 || dimsMissing || weightMissing) {
        blockedIds.push(id);
        const name = draft.item_name || (itemLike && itemLike.item_name) || "New item";
        blockedNames.push(name);
        // Human-readable reasons for this item (drives the Save-button tooltip).
        const reasons = [];
        bad.forEach((b) => {
          if (b.code === "piece_split_mismatch") reasons.push("Piece split mismatch");
          else if (b.code === "weight_mismatch") reasons.push("Unit weights don't add up");
          else reasons.push(b.message || "Error");
        });
        if (weightMissing) reasons.push("Weight required");
        if (dimsMissing) reasons.push("Dimensions required");
        blocked.push({ name, reasons });
      }
    };
    // EVERY item is checked — not just dirty/unit-edited ones. A mismatch can
    // already be PERSISTED (unit split edits PATCH immediately in normal
    // mode), in which case the card shows the warning chip while its draft is
    // completely clean; the Save gate must agree with the chips it renders
    // next to, or Save "allows saving with an error on screen".
    // visibleItems, not items — a staged-deleted item must never block Save.
    visibleItems.forEach((item) => {
      // Audit fix I3: an item just Saved clean (its draft cleared, its
      // pendingUnitEdits flushed) sits in mismatchSuppressedIds until the
      // post-Save reload lands — same window ItemCard's own effectiveMismatches
      // now suppresses. Checking it here too keeps the Save-button gate and
      // the "can't save" tooltip from ever naming an item that was just
      // saved and is only waiting on its reload to confirm it's clean.
      if (mismatchSuppressedIds.has(item.id)) return;
      const entry = itemDrafts[item.id];
      checkItem({
        id: item.id,
        itemLike: item,
        draft: entry?.draft || buildDraft(item),
        units: item.units || [],
      });
    });
    pendingNewItems.forEach((p) => {
      if (isEmptyDraft(p.draft) && !hasPendingUnitEditsEntry(pendingUnitEdits[p.id])) return;
      checkItem({ id: p.id, itemLike: null, draft: p.draft, units: [] });
    });
    return { blockedIds, blockedNames, blocked };
  };

  // Audit fix I1(b): computed once so both the Save-button `disabled` gate
  // and its tooltip (which now NAMES the offending items instead of a
  // generic message) read the exact same list.
  const blockedInfo = useMemo(
    () => collectBlockedItems(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, itemDrafts, pendingNewItems, pendingUnitEdits, mismatchSuppressedIds, requiredDims, requireItems]
  );
  const saveBlocked = blockedInfo.blockedIds.length > 0;

  // Report blocking state to the surrounding order form (create / entire
  // edit), which disables its own Save and aborts submit while true. Fires on
  // mount and whenever it flips; the parent stores it in state, so re-invoking
  // with the same value is a no-op there.
  // Second arg = a detailed, per-item reason string so the surrounding Create/
  // Save button can show WHICH items need what (user request), not just disable.
  useEffect(() => {
    if (typeof onBlockedChange === "function") {
      onBlockedChange(saveBlocked, saveBlocked ? formatBlockedDetails(blockedInfo.blocked) : "");
    }
  }, [saveBlocked, onBlockedChange, blockedInfo]);

  // "Can't save — fix piece split / weight on: A, B, C" — capped at 3 named
  // items (+N more) so the tooltip stays short even with a large blocked set.
  function formatBlockedTooltip(names) {
    if (!names || names.length === 0) return "";
    const shown = names.slice(0, 3);
    const rest = names.length - shown.length;
    const list = shown.join(", ") + (rest > 0 ? `, +${rest} more` : "");
    return `Can't save — fix piece split / weight on: ${list}`;
  }

  // Detailed multi-line reason for the surrounding Create/Save button's tooltip:
  // each blocked item named with what it needs ("• Chair: Weight required,
  // Dimensions required"). Capped at 4 items (+N more) so it stays readable.
  function formatBlockedDetails(blocked) {
    if (!blocked || blocked.length === 0) return "";
    const shown = blocked.slice(0, 4);
    const rest = blocked.length - shown.length;
    const lines = shown.map((b) => `• ${b.name}: ${b.reasons.join(", ")}`);
    if (rest > 0) lines.push(`• +${rest} more`);
    return `Fix these before saving:\n${lines.join("\n")}`;
  }

  const handleSaveAll = async () => {
    if (savingAll) return;

    // visibleItems, not items — an item staged for deletion is never updated,
    // its unit edits never flushed; it's deleted at the end of this Save.
    const dirtyEntries = visibleItems
      .map((item) => ({ item, entry: itemDrafts[item.id] }))
      .filter(({ item, entry }) => entry && !draftEquals(entry.draft, buildDraft(item)));
    const dirtyIds = new Set(dirtyEntries.map((d) => d.item.id));
    // Draft is clean but the user typed into this item's units table while it
    // was previously previewing — still needs its pending unit edits flushed,
    // even though there's no item-level updateItem call to make for it.
    const unitOnlyItems = visibleItems.filter(
      (item) => !dirtyIds.has(item.id) && hasPendingUnitEditsEntry(pendingUnitEdits[item.id])
    );
    // Audit fix C2: a "unit-only" item with pending ADDED seed edits but
    // fewer real units than its own draft's target HU count is the legacy
    // no-units bug — with no item-level updateItem call, OrderItem's
    // after_save unit-sync hook never runs, the reload still shows zero
    // real units, and the flush step below would silently drop every typed
    // value (its target index never exists in the reloaded, seq-sorted
    // list). Promote any such item into the ordinary update batch: sending
    // its (otherwise-unchanged) payload still triggers
    // OrderItemUnitSyncService via the after_save hook, which grows the
    // item's real units so the flush step has something to resolve against.
    const targetHandlingUnits = (item) => {
      const entry = itemDrafts[item.id];
      const draft = entry ? entry.draft : buildDraft(item);
      const n = parseInt(draft.handling_units, 10);
      return Number.isFinite(n) ? n : (item.units || []).length;
    };
    const unitOnlyNeedsSync = unitOnlyItems.filter((item) => {
      const pending = pendingUnitEdits[item.id];
      const hasAdded = !!pending && Object.keys(pending.added || {}).length > 0;
      return hasAdded && (item.units || []).length < targetHandlingUnits(item);
    });
    const unitOnlyNeedsSyncIds = new Set(unitOnlyNeedsSync.map((item) => item.id));
    // Everything else in unitOnlyItems either has no `added` edits, or
    // already has enough real units to resolve them against — no promotion
    // needed, same "no item-level call, just flush" path as before.
    const unitOnlyFlushOnly = unitOnlyItems.filter((item) => !unitOnlyNeedsSyncIds.has(item.id));
    const emptyNewIds = pendingNewItems
      .filter((p) => isEmptyDraft(p.draft) && !hasPendingUnitEditsEntry(pendingUnitEdits[p.id]))
      .map((p) => p.id);
    const newItemsToCreate = pendingNewItems.filter(
      (p) => !isEmptyDraft(p.draft) || hasPendingUnitEditsEntry(pendingUnitEdits[p.id])
    );

    if (
      dirtyEntries.length === 0 &&
      newItemsToCreate.length === 0 &&
      unitOnlyItems.length === 0 &&
      pendingDeleteIds.size === 0
    ) {
      // Nothing real to persist — just drop any still-empty draft cards and
      // behave like "Done" (re-lock the view-screen box, if applicable).
      if (emptyNewIds.length > 0) {
        setPendingNewItems((prev) => prev.filter((p) => !emptyNewIds.includes(p.id)));
      }
      if (canInlineEdit) setInlineEditing(false);
      // IM1: nothing failed — a surrounding order-save flush may proceed.
      return true;
    }

    // Save gate (user requirement): an item showing "This item can't be
    // closed yet." (piece-split or weight mismatch) must NOT save. The header
    // button is already disabled while blocked (collectBlockedItems memo
    // below), so this is belt-and-braces against a stale render's click:
    // abort before a single API call, expand the offending cards, surface
    // the errors.
    const { blockedIds, blockedNames } = collectBlockedItems();
    if (blockedIds.length > 0) {
      setCollapsedItems((prev) => {
        const next = { ...prev };
        blockedIds.forEach((id) => {
          next[id] = false;
        });
        return next;
      });
      renderAlertMessage(
        blockedNames.map((name) => `${name}: this item can't be closed yet — fix the piece split / weight mismatch before saving.`)
      );
      // IM1: blocked — signal failure so a surrounding order-save flush aborts.
      return false;
    }

    setSavingAll(true);
    const orgId = currentOrg && currentOrg.id;

    // Snapshot BEFORE saving: how many real units each item has right now,
    // so the flush step below can tell "added[k]" apart from "existing" once
    // the reloaded list has grown/shrunk — added[k] is always the unit at
    // index preSaveCount + k in the reloaded, seq-sorted list. 0 for a
    // pending new item card (it has no units at all yet).
    const preSaveUnitCounts = {};
    dirtyEntries.forEach(({ item }) => {
      preSaveUnitCounts[item.id] = (item.units || []).length;
    });
    unitOnlyItems.forEach((item) => {
      preSaveUnitCounts[item.id] = (item.units || []).length;
    });
    newItemsToCreate.forEach((p) => {
      preSaveUnitCounts[p.id] = 0;
    });

    try {
      // Audit fix C2: unitOnlyNeedsSync items ride the same batch as
      // ordinarily-dirty items — their "entry" just falls back to the
      // untouched buildDraft(item) baseline (there's nothing dirty to send,
      // the payload exists purely to fire the after_save unit-sync hook).
      const updateTargets = [
        ...dirtyEntries,
        ...unitOnlyNeedsSync.map((item) => ({
          item,
          entry: itemDrafts[item.id] || { draft: buildDraft(item), touched: {} },
        })),
      ];

      // Performance (user request): ONE batch call for the whole Save instead
      // of one PUT/POST update_customer_order_item per item — the old fan-out
      // re-ran the order-level side effects (weight rollup, quotation, BOL,
      // realtime broadcast) once PER item. Every dirty/promoted item goes in
      // `items` (keyed by its real id), every new card in `new_items` (keyed by
      // a client_ref = its temp id, which the server echoes back so creates can
      // be mapped to their real ids below). The server runs those side effects
      // exactly once and returns the fresh order_items + summary.
      const itemsPayload = updateTargets.map(({ item, entry }) =>
        buildItemPayload({ item, draft: entry.draft, touched: entry.touched, orderId, orgId, accountId })
      );
      const newItemsPayload = newItemsToCreate.map((p) => ({
        ...buildNewItemPayload({ draft: p.draft, touched: p.touched, orderId, orgId, accountId }),
        client_ref: p.id,
      }));

      // Name lookup for the partial-failure alert below — keyed by the id each
      // failure carries (item.id for updates, the pending card's temp id /
      // client_ref for creates) so a failed item can be traced to a
      // human-readable name instead of a bare, unattributed server error.
      const nameById = {};
      updateTargets.forEach(({ item }) => {
        nameById[item.id] = item.item_name || item.item_model || "Item";
      });
      newItemsToCreate.forEach((p) => {
        nameById[p.id] = p.draft.item_name || "New item";
      });

      // Skip the round-trip entirely when there's no item-level work (a
      // unit-only Save) — the flush phase below still runs. Otherwise one call.
      const batchRan = itemsPayload.length > 0 || newItemsPayload.length > 0;
      let batchResult = { success: true, items: [], item_errors: [], created: [] };
      if (batchRan) {
        batchResult = await saveOrderItems({
          customer_order_id: orderId,
          org_id: orgId,
          account_id: accountId,
          from_units_screen: "true",
          items: itemsPayload,
          new_items: newItemsPayload,
        });
        if (!isMounted.current) return;
        // Whole-batch failure (order not found, or every item errored) — keep
        // all drafts/pending edits so nothing typed is lost, surface the error,
        // and abort before the flush phase (the items never persisted).
        if (!batchResult.success) {
          renderAlertMessage(batchResult.errors || ["Failed to save items."]);
          // IM1: whole-batch failure (nothing persisted) — signal failure so a
          // surrounding order-save flush does NOT proceed to save the order
          // header while the items silently didn't save.
          return false;
        }
      }

      // Per-item failures come back in item_errors (successes still applied).
      const itemErrors = batchResult.item_errors || [];
      const failedUpdateIds = new Set(
        itemErrors.filter((e) => e.item_id != null).map((e) => String(e.item_id))
      );
      const failedCreateRefs = new Set(
        itemErrors.filter((e) => e.client_ref != null).map((e) => String(e.client_ref))
      );
      // Shape kept compatible with the old `failed` list (id + result.errors)
      // so the alert + collapse code downstream is unchanged.
      const failed = itemErrors.map((e) => ({
        id: e.item_id != null ? e.item_id : e.client_ref,
        result: { errors: e.errors || [] },
      }));
      const succeededUpdateIds = new Set(
        updateTargets.map((t) => t.item.id).filter((id) => !failedUpdateIds.has(String(id)))
      );
      const succeededCreateIds = new Set(
        newItemsToCreate.map((p) => p.id).filter((ref) => !failedCreateRefs.has(String(ref)))
      );
      // client_ref (temp id) -> real item id for each successful create, from
      // the server's `created` map — resolves temp-id pending unit edits below.
      const createIdMap = {};
      (batchResult.created || []).forEach((c) => {
        if (c && c.client_ref != null && c.id != null) createIdMap[String(c.client_ref)] = c.id;
      });

      // The authoritative item+unit list this Save produced — used to resolve
      // the flush targets and the collapse pass below WITHOUT a reload GET.
      // When the batch didn't run (a unit-only Save), nothing item-level changed
      // so the current on-screen list is already authoritative.
      const freshItems = batchRan ? batchResult.items || [] : itemsRef.current || [];

      if (succeededUpdateIds.size > 0) {
        setItemDrafts((prev) => {
          const next = { ...prev };
          succeededUpdateIds.forEach((id) => delete next[id]);
          return next;
        });
      }
      setPendingNewItems((prev) => prev.filter((p) => !emptyNewIds.includes(p.id) && !succeededCreateIds.has(p.id)));

      if (failed.length > 0) {
        // Name each failed item (matching the blocked-gate style above)
        // instead of concatenating raw server errors with no attribution —
        // a partial failure must say WHICH item didn't save.
        renderAlertMessage(
          failed.reduce((all, r) => {
            const name = nameById[r.id] || "Item";
            const errs = r.result.errors || [];
            const messages = errs.length > 0 ? errs.map((e) => `${name}: ${e}`) : [`${name}: failed to save`];
            return all.concat(messages);
          }, [])
        );
      } else if (canInlineEdit) {
        setInlineEditing(false);
      }

      // Refresh the whole screen straight from the batch response — the item
      // save now returns the full index-style payload (summary,
      // account_dim_factor, items+units), so there's no follow-up reload GET.
      // Fresh items carry fresh server mismatches, so there's no stale-mismatch
      // window to suppress; clear any leftover suppression from a prior save.
      if (batchRan) applyUnitsPayload(batchResult);
      if (isMounted.current) {
        setMismatchSuppressedIds((prev) => (prev.size > 0 ? new Set() : prev));
      }

      // QA fix wave 2 / F3: "click Save and every item box collapses."
      // Collapse every PERSISTED item on screen (a clean box collapses too),
      // except one whose save just failed (kept expanded to fix/retry) or whose
      // fresh mismatches are non-empty (blocking wins). Reads freshItems (the
      // write response) rather than itemsRef.current, which the mirror effect
      // hasn't refreshed yet now that we no longer reload.
      if (isMounted.current) {
        const failedItemIds = new Set(failed.map((r) => r.id));
        setCollapsedItems((prev) => {
          const next = { ...prev };
          freshItems.forEach((it) => {
            if (failedItemIds.has(it.id)) return;
            const blocking = it.mismatches || [];
            if (blocking.length > 0) return;
            next[it.id] = true;
          });
          return next;
        });
      }

      // ---- Phase 2: flush pending unit edits against reloaded data ----
      // Eligible = every item whose item-level operation (if any) succeeded:
      // a dirty item's update succeeded (this now also covers a promoted
      // unitOnlyNeedsSync item — its id lands in succeededUpdateIds exactly
      // like any other successful update), a unit-only item with no
      // item-level call to begin with (unitOnlyFlushOnly — trivially
      // "succeeded"), or a pending-new-item's create succeeded (resolved to
      // its real id via createIdMap). A failed dirty item or failed create is
      // excluded — its pendingUnitEdits stay put, same "keep the failed
      // draft" convention as itemDrafts.
      const eligibleKeys = [
        ...succeededUpdateIds,
        ...unitOnlyFlushOnly.map((item) => item.id),
        ...Object.keys(createIdMap),
      ];

      // Audit fix C2(b): item name for the "couldn't apply unit edits"
      // message below — falls back through the reloaded item, the item's own
      // draft, and a pending-new-item's draft, in that order.
      const nameForPendingKey = (key) => {
        const realId = createIdMap[key] || key;
        const reloadedItem = freshItems.find((it) => String(it.id) === String(realId));
        if (reloadedItem) return reloadedItem.item_name || reloadedItem.item_description || "Untitled Item";
        const entry = itemDrafts[key];
        if (entry) return entry.draft.item_name || "Untitled Item";
        const pending = pendingNewItems.find((p) => p.id === key);
        if (pending) return pending.draft.item_name || "New item";
        return "Item";
      };

      const flushOps = [];
      // Audit fix C2(b): unlike a shrunk-away EXISTING unit (silently
      // skipped below — that unit is legitimately gone, nothing to apply to,
      // ever), an ADDED seed edit with no matching post-reload unit means
      // Save never actually created the row this edit was meant for — the
      // exact C2 failure mode. Tracked separately so its whole
      // pendingUnitEdits entry survives instead of being deleted, with one
      // error surfaced per affected item.
      const unresolvedAddedKeys = new Set();
      eligibleKeys.forEach((pendingKey) => {
        const pendingEntry = pendingUnitEdits[pendingKey];
        if (!pendingEntry) return;
        const realItemId = createIdMap[pendingKey] || pendingKey;
        const freshItem = freshItems.find((it) => String(it.id) === String(realItemId));
        const freshUnits = (freshItem ? freshItem.units || [] : [])
          .slice()
          .sort((a, b) => (parseInt(a.seq, 10) || 0) - (parseInt(b.seq, 10) || 0));
        const preSaveCount = preSaveUnitCounts[pendingKey] != null ? preSaveUnitCounts[pendingKey] : 0;

        Object.keys(pendingEntry.existing || {}).forEach((unitId) => {
          // The HU count may have shrunk and soft-deleted this unit — skip
          // silently rather than PATCHing an id that's now gone.
          if (freshUnits.some((u) => String(u.id) === String(unitId))) {
            flushOps.push({ pendingKey, unitId, fields: pendingEntry.existing[unitId] });
          }
        });
        Object.keys(pendingEntry.added || {}).forEach((idxStr) => {
          const idx = parseInt(idxStr, 10);
          const target = freshUnits[preSaveCount + idx];
          if (target) {
            flushOps.push({ pendingKey, unitId: target.id, fields: pendingEntry.added[idxStr] });
          } else {
            unresolvedAddedKeys.add(pendingKey);
          }
        });
      });

      // Batch the flush into ONE bulk_upsert call (heterogeneous per-unit
      // attrs) instead of one PATCH per unit — the affected items rebalance
      // once and the order rolls up once, server-side. bulk_upsert
      // pre-validates every unit before any write, so it's effectively
      // all-or-nothing: on success every flushOp landed, on failure none did,
      // and the whole set of flushed keys stays pending for retry.
      let flushOk = true;
      if (flushOps.length > 0) {
        const bulkResult = await bulkUpsertUnits(
          flushOps.map((op) => ({ unit_id: op.unitId, ...op.fields }))
        );
        if (!isMounted.current) return;
        flushOk = !!bulkResult.success;
        if (!flushOk) {
          renderAlertMessage(bulkResult.errors || ["Couldn't apply unit edits — try again."]);
        } else {
          // bulk_upsert returns the full index-style payload (all items+units,
          // incl. rebalanced siblings) — refresh the screen from it directly,
          // no reload GET.
          applyUnitsPayload(bulkResult);
        }
      }

      if (flushOps.length > 0 || eligibleKeys.length > 0) {
        // Audit fix C2(b): a key is fully resolved only when the flush
        // succeeded AND it had no unresolved "added" target (Save never
        // created the row that edit was for). A failed flush keeps every
        // flushed key; an unresolved-added key is always kept — nothing typed
        // is silently dropped, and one message per affected item is surfaced.
        const flushFailedKeys = flushOk ? new Set() : new Set(flushOps.map((op) => op.pendingKey));
        const unresolvedKeys = new Set([...flushFailedKeys, ...unresolvedAddedKeys]);
        if (unresolvedAddedKeys.size > 0) {
          renderAlertMessage(
            Array.from(unresolvedAddedKeys).map(
              (key) => `Couldn't apply unit edits for ${nameForPendingKey(key)} — try again.`
            )
          );
        }
        setPendingUnitEdits((prev) => {
          const next = { ...prev };
          eligibleKeys.forEach((key) => {
            if (!unresolvedKeys.has(key)) delete next[key];
          });
          return next;
        });
        // No reload GET: the flush's bulk_upsert response (applied above via
        // applyUnitsPayload) already carried the fresh LPNs/seqs and the
        // re-derived sibling units.
      }

      // ---- Phase 3: staged deletes (deferred inline-edit removals) ----
      // Persisted last — the UI/summary already reflected the removal via
      // visibleItems. A final reload reconciles the authoritative list; only
      // items whose delete failed stay staged (still hidden, retryable).
      if (pendingDeleteIds.size > 0) {
        const idsToDelete = Array.from(pendingDeleteIds);
        const delResults = await Promise.all(idsToDelete.map((id) => deleteItem(id, true)));
        if (!isMounted.current) return;
        const failedDeleteIds = idsToDelete.filter((id, i) => !(delResults[i] && delResults[i].success));
        setPendingDeleteIds(new Set(failedDeleteIds));
        if (failedDeleteIds.length > 0) {
          renderAlertMessage(["Couldn't delete some items — please try again."]);
        }
        // deleteItem returns no index payload, so re-fetch the authoritative
        // post-delete item/unit list + order rollup.
        await loadUnits();
      }
    } finally {
      if (isMounted.current) setSavingAll(false);
    }
  };

  // QA fix wave 2 / F2: always route through handleSaveAll — it already
  // handles the "nothing dirty" case exactly like the old "Done" click did
  // (drops empty pending-new cards and re-locks when canInlineEdit, see its
  // early-return branch), so there is no separate re-lock-only path needed
  // here anymore.
  const handleHeaderButtonClick = () => {
    // Inline-update path (order-details screen "Save"): after this panel's save
    // resolves, refresh the parent so the order-level figures shown OUTSIDE this
    // panel — the weight / item-count in the order-details header — reflect the
    // just-saved rollup. The entire-edit flow already refreshes via the order
    // form after it awaits saveRef, so this only covers the inline path.
    Promise.resolve(handleSaveAll())
      .then(() => {
        if (refreshDetails) refreshDetails();
      })
      .catch(() => {});
  };

  // Entire-edit hook: keep the exposed save pointed at the latest handleSaveAll
  // (recreated each render) so the surrounding order form can flush this box's
  // drafts on its own Save. Returns handleSaveAll's promise so the form can
  // await it before updating the order.
  if (saveRef) saveRef.current = handleSaveAll;

  // Cancel the inline-edit session (view screen, pencil-unlocked): discard
  // every unsaved edit — field drafts, new-item cards, deferred unit edits,
  // live overlays — re-lock the box, and reload the authoritative persisted
  // values so nothing half-typed lingers on screen.
  const handleCancelInlineEdit = () => {
    setItemDrafts({});
    setPendingNewItems([]);
    setPendingUnitEdits({});
    setLiveUnitValues({});
    // Discard staged (deferred) deletes too — Cancel must never delete anything.
    setPendingDeleteIds(new Set());
    setInlineEditing(false);
    loadUnits();
  };

  // Cancel a single item card's edits (the per-card Cancel next to Done):
  // drop that item's field draft and any deferred unit edits (reverting it to
  // the persisted values) and collapse it. New/unsaved cards use onRemoveNew
  // instead (handled in ItemCard).
  const handleCancelItemEdit = (itemId) => {
    setItemDrafts((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setPendingUnitEdits((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setCollapsedItems((prev) => ({ ...prev, [itemId]: true }));
  };

  const contextValue = useMemo(
    () => ({
      reload: loadUnits,
      orderId,
      // Live LPN preview inputs for not-yet-saved unit rows (UnitRow).
      orderNumber,
      warehouseCode,
      currentOrg,
      accountId,
      actionType,
      typeOfOrder,
      orgSettings,
      screenFrom,
      config,
      form,
      refreshDetails,
      updateItemDetails,
      showActions,
      // Context exposes the EFFECTIVE flag (post inline-edit-toggle), same as
      // every readOnly prop threaded explicitly below, so any future
      // context-only consumer stays consistent with what's on screen.
      readOnly: inputsLocked,
      // Full items (each with its own `units`) so any UnitRow — whether
      // rendered inside a per-item UnitsTable or the cross-item
      // FlatUnitsView — can build its own "swap label with…" options from
      // every OTHER item's units without every level of the tree having to
      // drill it down separately.
      items,
      // Display-only live values for the top-panel rollups — UnitRow calls this
      // on every keystroke so Booked/Re-Weight/Re-Dim/Chargeable update live.
      setLiveUnitValue,
      // Task 7: counterpart UnitRow calls on a failed save to drop this
      // unit's live overlay so the rollups revert to server state.
      clearLiveUnitValue,
      // Inline-edit deferral: ItemCard reads deferMutations to decide whether a
      // persisted item's Delete/Duplicate stages locally (no API until Save) or
      // hits the API immediately (entire-edit / other flows).
      deferMutations,
      stageDeleteItem,
      stageDuplicateItem,
      // Opens the shared order-level label popup (UnitRow / BulkApplyRow call
      // this when print_label_based_on_lpn is OFF).
      openOrderLabelPopup,
    }),
    [
      loadUnits,
      orderId,
      orderNumber,
      warehouseCode,
      currentOrg,
      accountId,
      actionType,
      typeOfOrder,
      orgSettings,
      screenFrom,
      config,
      form,
      refreshDetails,
      updateItemDetails,
      showActions,
      inputsLocked,
      items,
      setLiveUnitValue,
      clearLiveUnitValue,
      deferMutations,
      openOrderLabelPopup,
    ]
  );

  // Header button, two states (QA fix wave 2 / F2 removes the old "Done"
  // label — the unlocked button ALWAYS reads "Save" regardless of dirty
  // state, since clicking it always safely routes through handleSaveAll):
  //  - view screen, locked (effectiveReadOnly): "Edit" (only if canInlineEdit).
  //  - unlocked (canInlineEdit && inlineEditing, OR a natively-editable
  //    screen where canInlineEdit is false and the box was never readOnly):
  //    "Save" — persists any pending drafts and re-locks (view screen) or
  //    just re-locks/no-ops when nothing is dirty (natively-editable screens
  //    disable the button in that case instead, same as before).
  // No box-local Save/Edit button during CREATE (no order yet) — the
  // surrounding Create Order screen owns saving; this box just collects item
  // details via getDataRef. Only shown once the order exists.
  const headerButtonVisible = !!orderId && (canInlineEdit || !readOnly);
  const headerButtonLabel = "Save";
  // Save is DISABLED (not just click-blocked) while any to-be-saved item has
  // a live piece-split/weight mismatch — the mockup's "can't be closed yet".
  const headerButtonDisabled = savingAll || (!isDirty && !canInlineEdit) || saveBlocked;

  // Shared per-persisted-item ItemCard props (normal Items-view render,
  // whether flat or clustered under a GROUP N / UNGROUPED label below) —
  // factored out so the saved-grouping cluster branch doesn't have to repeat
  // this whole prop block a second time.
  // Latest handlers snapshot (updated every render) read by the stable per-item
  // callback bundle below — keeps the bundle's identity fixed while never
  // capturing stale state. Assigned here, after every handler is initialized.
  latestHandlersRef.current = {
    handleItemFieldChange,
    handleItemSelectChange,
    toggleCollapse,
    handleCancelItemEdit,
    setUnitPendingEdit,
  };
  // Stable per-item callback bundle: created once per item id and reused, so
  // ItemCard sees the same function references across renders. Each wrapper
  // dispatches through latestHandlersRef so it always calls this render's
  // handler (no stale closure).
  const getCardCbs = (itemId) => {
    let bundle = cardCbRef.current.get(itemId);
    if (!bundle) {
      bundle = {
        onFieldChange: (field, value) => latestHandlersRef.current.handleItemFieldChange(itemId, field, value),
        onSelectChange: (field, value) => latestHandlersRef.current.handleItemSelectChange(itemId, field, value),
        onToggleCollapse: () => latestHandlersRef.current.toggleCollapse(itemId),
        onCancelEdit: () => latestHandlersRef.current.handleCancelItemEdit(itemId),
        onUnitPendingEdit: (kind, key, fields) => latestHandlersRef.current.setUnitPendingEdit(itemId, kind, key, fields),
      };
      cardCbRef.current.set(itemId, bundle);
    }
    return bundle;
  };

  const renderPersistedItemCard = (item) => {
    const cbs = getCardCbs(item.id);
    const draftEntry = itemDrafts[item.id];
    return (
      <ItemCard
        key={item.id}
        item={item}
        units={item.units || EMPTY_ARRAY}
        dimFactor={dimFactor}
        configuredDimFactors={configuredDimFactors}
        accountDimFactor={accountDimFactor}
        mismatches={mismatchSuppressedIds.has(item.id) ? EMPTY_ARRAY : item.mismatches || EMPTY_ARRAY}
        readOnly={inputsLocked}
        draft={draftEntry?.draft || getFallbackDraft(item)}
        touched={draftEntry?.touched || EMPTY_OBJECT}
        onFieldChange={cbs.onFieldChange}
        onSelectChange={cbs.onSelectChange}
        collapsed={isItemCollapsed(item.id)}
        onToggleCollapse={cbs.onToggleCollapse}
        onCancelEdit={cbs.onCancelEdit}
        pendingUnitEdits={pendingUnitEdits[item.id] || EMPTY_ITEM_UNIT_EDITS}
        onUnitPendingEdit={cbs.onUnitPendingEdit}
        suppressMismatches={mismatchSuppressedIds.has(item.id)}
        hiddenFields={hiddenFields}
        // Per-item live rollup so the card's own WT/DIM/RE-WT/RE-DIM meta tracks
        // unit edits as they're typed, same as the top SummaryBar.
        liveMeta={getLiveMeta(item)}
        // PO point 7: highlight the card while it has unsaved edits — a dirty
        // item draft, deferred pending unit edits, OR live scannable-unit typing
        // (dirtyCardIds folds in liveUnitValues). Clears once Save's reload lands.
        dirty={dirtyCardIds.has(item.id)}
        dimWeightBasis={dimWeightBasis}
        requiredDims={requiredDims}
        requireItems={requireItems}
      />
    );
  };

  // Group-mode row (checkbox card, no editing) — readOnly is forced true
  // (nothing edits while selecting for grouping) and the draft/units/
  // pendingUnitEdits plumbing collapses to the persisted values directly,
  // since ItemCard's groupMode branch never reads past what it needs to
  // render the reduced row (see ItemCard.jsx).
  const renderGroupModeItemCard = (item) => (
    <ItemCard
      key={item.id}
      item={item}
      units={item.units || []}
      mismatches={[]}
      readOnly
      draft={buildDraft(item)}
      touched={{}}
      onFieldChange={() => {}}
      onSelectChange={() => {}}
      collapsed
      onToggleCollapse={() => {}}
      groupMode
      selected={selectedIds.has(item.id)}
      onToggleSelect={() => handleToggleSelectForGroup(item.id)}
    />
  );

  return (
    <ItemsMeasureUnitsContext.Provider value={contextValue}>
      {/* Shared order-level label popup for the non-LPN case of the per-unit
          "Print label" and bulk "Download labels" actions. No visible button of
          its own (isFromBulkContainer renders inline text, hidden here); its
          modal portals to body and opens when openOrderLabelPopup() fires. Only
          meaningful once the order exists. */}
      {orderId && (
        <span style={{ display: "none" }}>
          <PrintLabels
            isFromBulkContainer
            isPrintLabelClicked={labelPopupClicked}
            setIsPrintLabelClicked={setLabelPopupClicked}
            setIsHovered={() => {}}
            orderIds={[orderId]}
            handleSuccess={() => {}}
            label=""
            isDisabled={false}
            buttonType={null}
            size={null}
            buttonIcon={null}
          />
        </span>
      )}
      <div className="items-measure-units">
        <div className="app-header">
          <div className="title-group">
            <CubeIcon />
            <h1>Items &amp; Measure</h1>
          </div>
          <div className="header-actions">
            {groupMode ? (
              // Group Items active mode (Figma "Group Order" frames): the
              // Items/Scannable Units toggle and the box-local Edit/Save
              // button are both hidden — only the Ungroup/Group actions show,
              // exactly like the "State=Filled/Selected/Grouped/Ungroup
              // Selected" frames (none of them render the toggle or a pencil
              // icon).
              <React.Fragment>
                <button
                  type="button"
                  className="group-action-btn"
                  disabled={!ungroupEnabled || savingGroups}
                  onClick={handleUngroupSelected}
                >
                  <UngroupIcon /> {ungroupBtnLabel}
                </button>
                <button
                  type="button"
                  className="group-action-btn"
                  disabled={!groupEnabled || savingGroups}
                  onClick={handleGroupSelected}
                >
                  <GroupIcon /> {groupBtnLabel}
                </button>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {/* Items / Scannable Units toggle is meaningless before the
                    order exists (no units yet), so it only appears once
                    saved. */}
                {orderId && (
                  <div className="mode-toggle view-toggle">
                    <button
                      type="button"
                      className={`mode-btn ${viewMode === "items" ? "active" : ""}`}
                      onClick={() => setViewMode("items")}
                    >
                      Items
                    </button>
                    <button
                      type="button"
                      className={`mode-btn ${viewMode === "units" ? "active" : ""}`}
                      onClick={() => setViewMode("units")}
                    >
                      Scannable Units
                    </button>
                  </div>
                )}
                {groupItemsEntryVisible && (
                  <button type="button" className="group-action-btn" onClick={handleEnterGroupMode}>
                    <GroupIcon /> Group Items
                  </button>
                )}
                {/* Figma view frame (3689:27907): the header's only right-
                    hand affordance is an icon-only pencil in the TOP-right
                    corner. The Save button no longer lives here — while
                    editing it moves to the bottom-right action bar rendered
                    at the end of the container (per user request "Edit should
                    be in top right corner and save should be in down right
                    corner"). */}
                {headerButtonVisible && effectiveReadOnly && (
                  <Button
                    icon="edit"
                    size="small"
                    className="inline-edit-btn edit-icon-btn"
                    title="Edit"
                    onClick={() => setInlineEditing(true)}
                  />
                )}
              </React.Fragment>
            )}
          </div>
        </div>

        <SummaryBar
          summary={summary}
          declaredValue={declaredValue}
          onDeclaredValueSave={handleDeclaredValueSave}
          // Create flow: capture every keystroke live so the value is already
          // in state when the surrounding Create Order save reads getDataRef.
          onValueChange={!orderId ? setCreateDeclaredValue : undefined}
          hideDeclaredValue={hiddenFields.declared_value}
          readOnly={inputsLocked}
          saveDisabledReason={null}
          volumeFt3={volumeFt3}
          dimFactor={dimFactor}
          draftTotals={draftTotals}
          liveUnitTotals={liveUnitTotals}
          hasLiveUnitEdits={hasLiveUnitEdits}
        />

        {groupMode ? (
          // Group Items active mode (Figma "State=Filled/Selected/Grouped/
          // Ungroup Selected"): the prompt line, the checkbox item list
          // (clustered under GROUP N / UNGROUPED labels once any pending
          // group exists), and the Reset/Cancel/Save footer replace the
          // normal items/add-row/flat-view subtree entirely — group mode is
          // its own separate mode, not a variant of the Items view render
          // below.
          <React.Fragment>
            <p className="group-prompt">Select Items to Start Grouping Them:</p>
            <div className="items">
              {pendingGroupClusters.realGroups.map((group, idx) => (
                <div className="group-cluster" key={`pending-grp-${group[0].id}`}>
                  <div className="group-cluster-label">GROUP {idx + 1}</div>
                  {group.map(renderGroupModeItemCard)}
                </div>
              ))}
              {pendingGroupClusters.ungrouped.length > 0 && (
                <div className="group-cluster">
                  {pendingGroupClusters.realGroups.length > 0 && (
                    <div className="group-cluster-label">UNGROUPED</div>
                  )}
                  {pendingGroupClusters.ungrouped.map(renderGroupModeItemCard)}
                </div>
              )}
            </div>
            <div className="group-mode-footer">
              <button type="button" className="group-link-reset" onClick={handleResetGroups}>
                Reset
              </button>
              <button type="button" className="group-action-btn" onClick={handleCancelGroupMode}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={savingGroups}
                onClick={handleSaveGroups}
              >
                {savingGroups ? "Saving..." : "Save"}
              </button>
            </div>
          </React.Fragment>
        ) : orderId && loading && summary === null ? (
          // QA fix wave 2 / F5 (root-caused below): only swap the WHOLE
          // items/flat-view subtree for the spinner on the very FIRST load
          // (before any successful GET has landed — `summary` is null only
          // then, since it's set by loadUnits' success branch and never
          // reset afterwards). Every unit-level save (a blur PATCH, bulk
          // apply, swap, ...) calls reload() -> loadUnits(), which also
          // flips `loading` true then false around its own GET — previously
          // that ALWAYS swapped this subtree out for <Spin/> and back,
          // unmounting every ItemCard (and FlatUnitsView) mid-edit. Their
          // Booked/Re-Weighed mode, row selection, and collapse-attempt
          // state all live in plain useState in those components, so the
          // remount silently reset them to their initial values — reported
          // as "click Re-Weighed, type a value, tab out, and it flips back
          // to Booked." Gating the swap on `summary === null` means a
          // background reload just re-renders the SAME component instances
          // with fresh props (their local state untouched), exactly like
          // any other props update.
          <div className="items">
            <Spin />
          </div>
        ) : viewMode === "items" || !orderId ? (
          <React.Fragment>
            <div className="items">
              {/* Group Items (normal-mode display, Figma "Filled Grouped"
                  frame): once any items already share a saved item_group_id,
                  render every item clustered under GROUP N / UNGROUPED
                  labels instead of the flat list — same clustering helper
                  group mode itself uses, just keyed off the SERVER value.
                  With no saved grouping at all this is exactly the flat
                  `items.map` this screen always rendered. */}
              {savedGroupClusters.realGroups.length > 0 ? (
                <React.Fragment>
                  {savedGroupClusters.realGroups.map((group, idx) => (
                    <div className="group-cluster" key={`saved-grp-${group[0].id}`}>
                      <div className="group-cluster-label">GROUP {idx + 1}</div>
                      {group.map(renderPersistedItemCard)}
                    </div>
                  ))}
                  {savedGroupClusters.ungrouped.length > 0 && (
                    <div className="group-cluster">
                      <div className="group-cluster-label">UNGROUPED</div>
                      {savedGroupClusters.ungrouped.map(renderPersistedItemCard)}
                    </div>
                  )}
                </React.Fragment>
              ) : (
                visibleItems.map(renderPersistedItemCard)
              )}
              {pendingNewItems.map((p) => (
                <ItemCard
                  key={p.id}
                  isNew
                  item={{ id: p.id }}
                  units={[]}
                  dimFactor={dimFactor}
                  configuredDimFactors={configuredDimFactors}
                  accountDimFactor={accountDimFactor}
                  mismatches={[]}
                  readOnly={inputsLocked}
                  draft={p.draft}
                  touched={p.touched}
                  onFieldChange={(field, value) => handleNewItemFieldChange(p.id, field, value)}
                  onSelectChange={(field, value) => handleNewItemSelectChange(p.id, field, value)}
                  collapsed={isItemCollapsed(p.id, false)}
                  onToggleCollapse={() => toggleCollapse(p.id, false)}
                  onRemoveNew={() => handleRemoveNewItem(p.id)}
                  onDuplicateNew={() => handleDuplicateNewItem(p.id)}
                  // Units-during-create fix: keyed by this pending item's
                  // temp id, exactly like a persisted item's own slice —
                  // handleSaveAll's Phase 2 flush already resolves a
                  // pendingUnitEdits entry keyed by a temp id (via
                  // createIdMap, once its createItem call succeeds) against
                  // the real created item's reloaded units by seq order, so
                  // no further change was needed there.
                  pendingUnitEdits={pendingUnitEdits[p.id] || EMPTY_ITEM_UNIT_EDITS}
                  onUnitPendingEdit={(kind, key, fields) => setUnitPendingEdit(p.id, kind, key, fields)}
                  hiddenFields={hiddenFields}
                  // PO point 7: a not-yet-saved card is highlighted once the
                  // user has actually put something in it (a non-empty draft or
                  // pending unit edits), not while it's still a blank placeholder.
                  dirty={!isEmptyDraft(p.draft) || hasPendingUnitEditsEntry(pendingUnitEdits[p.id])}
                  dimWeightBasis={dimWeightBasis}
                  requiredDims={requiredDims}
                  requireItems={requireItems}
                />
              ))}
            </div>
            {!effectiveReadOnly && (
              <div className="add-row">
                <div className="qty-stepper">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={addQty}
                    onChange={(e) => setAddQty(e.target.value)}
                  />
                  <div className="steppers">
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() => setAddQty((q) => (parseInt(q, 10) || 1) + 1)}
                    >
                      &#9650;
                    </button>
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      onClick={() => setAddQty((q) => Math.max((parseInt(q, 10) || 1) - 1, 1))}
                    >
                      &#9660;
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddItems}
                >
                  Add {parseInt(addQty, 10) || 1} Item{(parseInt(addQty, 10) || 1) === 1 ? "" : "s"}
                </button>
              </div>
            )}
          </React.Fragment>
        ) : (
          <FlatUnitsView
            items={items}
            readOnly={inputsLocked}
            pendingUnitEdits={pendingUnitEdits}
            onUnitPendingEdit={setUnitPendingEdit}
          />
        )}

        {/* Bottom-right Save bar. Shown ONLY for the view-screen inline edit
            (canInlineEdit && inlineEditing) — i.e. a viewer unlocked just this
            box with the pencil. In the entire-order-edit flow (actionType
            "update", a natively-editable screen) and during create, the
            SURROUNDING order form owns saving and reads item data via
            getDataRef, so this box must NOT show its own Save (user request:
            "in entire order edit also, we shouldn't show the save button for
            that items and measure box"). Never in group mode (its own footer).
            Sticks to the viewport bottom; the Tooltip needs the span wrapper
            because disabled antd buttons swallow hover. */}
        {canInlineEdit && inlineEditing && !groupMode && (
          <div className="edit-action-bar">
            <Button
              className="inline-edit-btn cancel-bottom-btn"
              disabled={savingAll}
              onClick={handleCancelInlineEdit}
            >
              Cancel
            </Button>
            <Tooltip title={saveBlocked ? formatBlockedTooltip(blockedInfo.blockedNames) : ""}>
              <span>
                <Button
                  type="primary"
                  icon="save"
                  className="inline-edit-btn save-bottom-btn"
                  loading={savingAll}
                  disabled={headerButtonDisabled}
                  onClick={handleHeaderButtonClick}
                >
                  {headerButtonLabel}
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
      </div>
    </ItemsMeasureUnitsContext.Provider>
  );
};

export { ItemsMeasureUnitsContext, useItemsMeasureUnitsContext };
export default ItemsMeasureUnits;
